// src/pages/nascar/CupOptimizer.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import API_BASE from "../../utils/api";
import { useLocation, useNavigate } from "react-router-dom";

/* ---------------- LAST UPDATED (shared) ---------------- */
function useLastUpdated(mainUrl, metaUrl) {
  const [updatedAt, setUpdatedAt] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const h = await fetch(mainUrl, { method: "HEAD", cache: "no-store" });
        const lm = h.headers.get("last-modified");
        if (alive && lm) {
          setUpdatedAt(new Date(lm));
          return;
        }
      } catch (_) {}

      try {
        const r = await fetch(mainUrl, { cache: "no-store" });
        const lm2 = r.headers.get("last-modified");
        if (alive && lm2) {
          setUpdatedAt(new Date(lm2));
          return;
        }
      } catch (_) {}

      try {
        if (!metaUrl) return;
        const m = await fetch(`${metaUrl}?_=${Date.now()}`, { cache: "no-store" }).then((x) => x.json());
        const iso = m?.updated_iso || m?.updated_utc || m?.updated || m?.lastUpdated || m?.timestamp;
        const ep = m?.updated_epoch;
        const d = iso ? new Date(iso) : Number.isFinite(ep) ? new Date(ep * 1000) : null;
        if (alive && d && !isNaN(d)) setUpdatedAt(d);
      } catch (_) {}
    })();
    return () => {
      alive = false;
    };
  }, [mainUrl, metaUrl]);

  return updatedAt;
}

const fmtUpdated = (d) =>
  d
    ? d.toLocaleString(undefined, {
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

/* ----------------------------- helpers ----------------------------- */
const clamp = (v, lo = 0, hi = 1e9) => Math.max(lo, Math.min(hi, v));
const num = (v) => {
  const n = Number(String(v ?? "").toString().replace(/[, %]/g, ""));
  return Number.isFinite(n) ? n : 0;
};
const fmt0 = (n) => (Number.isFinite(n) ? n.toLocaleString() : "—");
const fmt1 = (n) => (Number.isFinite(n) ? n.toFixed(1) : "—");

const geoMeanPct = (items, accessor) => {
  const vals = (items || [])
    .map((x) => Number(accessor(x)))
    .filter((v) => Number.isFinite(v) && v > 0);

  if (!vals.length) return 0;

  const logAvg = vals.reduce((sum, v) => sum + Math.log(v), 0) / vals.length;
  return Math.exp(logAvg);
};

/* ----------------------------- UI THEME ----------------------------- */

const OPT_LIGHT_THEME = {
  page: "min-h-screen bg-slate-100 text-slate-900",
  headerText: "text-slate-900",
  subtext: "text-slate-600",
  card: "border border-slate-200 bg-white shadow-md",
  pill: "bg-white ring-slate-300",
  pillDarkBtnActive: "bg-sky-500 text-slate-950 shadow-sm",
  pillDarkBtnIdle: "text-slate-500 hover:text-slate-800",
  pillLightBtnActive: "bg-sky-500 text-slate-950 shadow-sm",
  pillLightBtnIdle: "text-slate-400 hover:text-slate-800",
  input: "bg-white border-slate-300 text-slate-800 placeholder:text-slate-400",
};

const OPT_DARK_THEME = {
  page: "min-h-screen bg-slate-950 text-slate-100",
  headerText: "text-slate-100",
  subtext: "text-slate-400",
  card: "bg-gradient-to-b from-slate-950 via-slate-950 to-slate-950/95 shadow-[0_20px_60px_rgba(15,23,42,0.9)]",
  pill: "bg-slate-900/90 ring-slate-700/80",
  pillDarkBtnActive: "bg-sky-500 text-slate-950 shadow-sm",
  pillDarkBtnIdle: "text-slate-400 hover:text-slate-100",
  pillLightBtnActive: "bg-sky-500 text-slate-950 shadow-sm",
  pillLightBtnIdle: "text-slate-500 hover:text-slate-200",
  input: "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500",
};

/* ------------------------ localStorage helpers ------------------------ */
function isQuotaError(e) {
  const name = e?.name || "";
  return name === "QuotaExceededError" || name === "NS_ERROR_DOM_QUOTA_REACHED";
}

// ✅ Rehydrate safely + never overwrite saved values on mount
// ✅ If localStorage quota is hit on "builds", prune old builds and retry.
const useStickyState = (key, init) => {
  const getInit = React.useCallback(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw != null) return JSON.parse(raw);
    } catch (_) {}
    return typeof init === "function" ? init() : init;
  }, [key, init]);

  const [v, setV] = React.useState(() => getInit());

  // Rehydrate when the key changes (DK ↔ FD, Cup ↔ Xfinity ↔ Trucks)
  const keyRef = React.useRef(key);
  React.useEffect(() => {
    if (keyRef.current !== key) {
      keyRef.current = key;
      setV(getInit());
    }
  }, [key, getInit]);

  // Persist (with optional pruning for builds on quota)
  React.useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(v));
    } catch (e) {
      // If builds are too large, prune to last 20 and retry once
      if (isQuotaError(e) && /\.builds$/.test(key) && Array.isArray(v)) {
        try {
          const pruned = v.slice(Math.max(0, v.length - 20));
          localStorage.setItem(key, JSON.stringify(pruned));
          if (pruned.length !== v.length) setV(pruned);
        } catch (_) {}
        return;
      }
    }
  }, [key, v]);

  return [v, setV];
};

const escapeCSV = (s) => {
  const v = String(s ?? "");
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
};

// Normalize driver names for matching vs site_ids
const normName = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/\u2019/g, "'")
    .replace(/\./g, "")
    .replace(/,\s*(jr|sr)\b/g, "")
    .replace(/\b(jr|sr)\b/g, "")
    .replace(/[^a-z' -]/g, "")
    .replace(/\s+/g, " ")
    .trim();

// Build a lookup from many possible name variants → { id, display, siteName, nameFromSite }
function buildSiteIdIndex(siteIdsList) {
  const idx = new Map();

  for (const r of siteIdsList || []) {
    const id = String(
      r.id ?? r.ID ?? r.playerId ?? r.player_id ?? r.fd_id ?? r.FD_ID ?? r.dk_id ?? r.DK_ID ?? ""
    ).trim();
    if (!id) continue;

    const display = r.display != null ? String(r.display).trim() : ""; // FD ID name
    const siteName = r.siteName != null ? String(r.siteName).trim() : ""; // DK ID name

    const legacy = (r.name ?? r.player ?? r.Player ?? r.displayName ?? r.Name) ?? "";
    const legacyStr = legacy != null ? String(legacy).trim() : "";

    const aliases = Array.isArray(r.aliases) ? r.aliases.map(String) : [];

    const candidates = [display, siteName, legacyStr, ...aliases].filter(Boolean);

    for (const nm of candidates) {
      const key = normName(nm);
      if (!key) continue;
      if (!idx.has(key)) {
        idx.set(key, {
          id,
          display,
          siteName,
          nameFromSite: nm,
        });
      }
    }
  }

  return idx;
}

// Try to discover FanDuel slate/group prefix
function detectFdPrefix(siteIdsList) {
  const counts = new Map();
  for (const r of siteIdsList || []) {
    const px = r.slateId ?? r.slate_id ?? r.groupId ?? r.group_id ?? r.lid ?? r.prefix ?? r.fd_prefix ?? null;
    if (px != null && px !== "") {
      const key = String(px);
      counts.set(key, (counts.get(key) || 0) + 1);
    }
  }
  if (counts.size === 1) return [...counts.keys()][0];
  if (counts.size > 1) return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
  return null;
}

/* ------------------------- sites & columns ------------------------- */
const SITES = {
  dk: {
    key: "dk",
    label: "DraftKings",
    logo: "/logos/dk.png",
    roster: 6,
    cap: 50000,
    salary: "DK Sal",
    proj: "DK Proj",
    floor: "DK Floor",
    ceil: "DK Ceiling",
    pown: ["DK pOWN%", "DK pOWN"],
    opt: ["DK Opt%", "DK Opt"],
  },
  fd: {
    key: "fd",
    label: "FanDuel",
    logo: "/logos/fd.png",
    roster: 5,
    cap: 50000,
    salary: "FD Sal",
    proj: "FD Proj",
    floor: "FD Floor",
    ceil: "FD Ceiling",
    pown: ["FD pOWN%", "FD pOWN"],
    opt: ["FD Opt%", "FD Opt"],
  },
};

// ---------------------- series sources ----------------------
const SERIES = {
  cup: {
    key: "cup",
    label: "Cup",
    projections: "/data/nascar/cup/latest/projections.json",
    siteIds: "/data/nascar/cup/latest/site_ids.json",
    driverData: "/data/nascar/cup/latest/data.json",
    route: "/nascar/cup/optimizer",
  },
  xfinity: {
    key: "xfinity",
    label: "O'Reilly",
    projections: "/data/nascar/xfinity/latest/projections.json",
    siteIds: "/data/nascar/xfinity/latest/site_ids.json",
    driverData: "/data/nascar/xfinity/latest/data.json",
    route: "/nascar/oreilly/optimizer",
  },
  trucks: {
    key: "trucks",
    label: "Trucks",
    projections: "/data/nascar/trucks/latest/projections.json",
    siteIds: "/data/nascar/trucks/latest/site_ids.json",
    driverData: "/data/nascar/trucks/latest/data.json",
    route: "/nascar/trucks/optimizer",
  },
};

/* ------------------------------ data ------------------------------- */
function useJson(url) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(url, { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => alive && (setData(j), setErr(null)))
      .catch((e) => alive && (setErr(e), setData(null)))
      .finally(() => alive && setLoading(false));
    return () => (alive = false);
  }, [url]);
  return { data, err, loading };
}

/* ---------------------------- CSV export --------------------------- */
function toPlainCSV(rows) {
  const header = ["#", "Salary", "Total", "Drivers"].join(",");
  const lines = rows.map((L, i) => {
    const drivers = `"${L.drivers.join(" • ")}"`;
    return [i + 1, L.salary, L.total.toFixed(1), drivers].join(",");
  });
  return [header, ...lines].join("\n");
}

function downloadPlainCSV(rows, fname = "lineups.csv") {
  const blob = new Blob([toPlainCSV(rows)], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}

/** Export (IDs only) with headers D (DK) or Driver (FD) — DK: "Name (id)"; FD: "prefix-id:Display Name" */
function downloadSiteLineupsCSV({ lineups, site, rosterSize, siteIds, fname = "lineups_site_ids.csv" }) {
  const siteKey = site === "fd" ? "fd" : "dk";

  const list = Array.isArray(siteIds?.[siteKey]) ? siteIds[siteKey] : siteIds?.sites?.[siteKey] ?? [];

  const idIndex = buildSiteIdIndex(list);
  const fdPrefix = siteKey === "fd" ? detectFdPrefix(list) : null;

  const header = Array.from({ length: rosterSize }, () => (siteKey === "fd" ? "Driver" : "D")).join(",");

  const lines = (lineups || []).map((L) => {
    const names = Array.isArray(L.drivers) ? L.drivers : [];

    const cells = names.slice(0, rosterSize).map((name) => {
      const rec = idIndex.get(normName(name));
      if (!rec) return escapeCSV(name);

      if (siteKey === "fd") {
        const outId = /^[a-z]+-\d+$/i.test(rec.id) ? rec.id : fdPrefix ? `${fdPrefix}-${rec.id}` : rec.id;
        const idName = rec.siteName || rec.display || rec.nameFromSite || name;
        return escapeCSV(`${outId}:${idName}`);
      }

      const idName = rec.siteName || rec.display || rec.nameFromSite || name;
      return escapeCSV(`${idName} (${rec.id})`);
    });

    while (cells.length < rosterSize) cells.push("");

    return cells.join(",");
  });

  const csv = [header, ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}

/** Detailed export: lineup meta + totals + drivers with IDs (DK: "Name (id)", FD: "prefix-id:Name") */
function downloadDetailedLineupsCSV({ lineups, site, rosterSize, siteIds, rows, fname = "lineups_detailed.csv" }) {
  if (!lineups?.length) return;

  const siteKey = site === "fd" ? "fd" : "dk";
  const list = Array.isArray(siteIds?.[siteKey]) ? siteIds[siteKey] : siteIds?.sites?.[siteKey] ?? [];

  const idIndex = buildSiteIdIndex(list);
  const fdPrefix = siteKey === "fd" ? detectFdPrefix(list) : null;

  const rowsByDriver = new Map((rows || []).map((r) => [r.driver, r]));

  const header = [
    "Lineup #",
    "Site",
    "Salary",
    "Total Proj",
    "Total Floor",
    "Total Ceiling",
    "Total pOWN%",
    "Total Opt%",
    ...Array.from({ length: rosterSize }, (_, i) => `Driver ${i + 1}`),
  ];

  const out = [header];

  (lineups || []).forEach((L, idx) => {
    const chosen = (Array.isArray(L.drivers) ? L.drivers : []).map((name) => rowsByDriver.get(name)).filter(Boolean);

    let totalProj = 0;
    let totalFloor = 0;
    let totalCeil = 0;
    let totalPown = 0;
    let totalOpt = 0;

    for (const r of chosen) {
      totalProj += r.proj || 0;
      totalFloor += r.floor || 0;
      totalCeil += r.ceil || 0;
      totalPown += (r.pown || 0) * 100;
      totalOpt += (r.opt || 0) * 100;
    }

    const base = [
      idx + 1,
      site.toUpperCase(),
      L.salary || 0,
      totalProj.toFixed(1),
      totalFloor.toFixed(1),
      totalCeil.toFixed(1),
      totalPown.toFixed(1),
      totalOpt.toFixed(1),
    ];

    const names = Array.isArray(L.drivers) ? L.drivers.slice(0, rosterSize) : [];
    const driverCells = names.map((name) => {
      const rec = idIndex.get(normName(name));
      if (!rec) return escapeCSV(name);

      if (siteKey === "fd") {
        const outId = /^[a-z]+-\d+$/i.test(rec.id) ? rec.id : fdPrefix ? `${fdPrefix}-${rec.id}` : rec.id;
        const idName = rec.siteName || rec.display || rec.nameFromSite || name;
        return escapeCSV(`${outId}:${idName}`);
      }

      const idName = rec.siteName || rec.display || rec.nameFromSite || name;
      return escapeCSV(`${idName} (${rec.id})`);
    });

    while (driverCells.length < rosterSize) driverCells.push("");

    out.push([...base, ...driverCells]);
  });

  const csv = out.map((row) => row.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadExposuresCSV(lineups, fname = "exposures.csv") {
  const m = new Map();
  for (const L of lineups) for (const d of L.drivers) m.set(d, (m.get(d) || 0) + 1);
  const total = Math.max(1, lineups.length);
  const rows = [...m.entries()]
    .map(([driver, cnt]) => [driver, cnt, (cnt / total) * 100])
    .sort((a, b) => b[2] - a[2] || a[0].localeCompare(b[0]));
  const header = "Driver,Count,Exposure %";
  const body = rows.map((r) => `${escapeCSV(r[0])},${r[1]},${r[2].toFixed(1)}`).join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}

/* -------------------- endpoints: auto-resolver ------------------ */
const SERIES_POST_PATHS = {
  cup: ["nascar/cup/solve", "cup/solve"],
  xfinity: ["nascar/xfinity/solve", "xfinity/solve"],
  trucks: ["nascar/trucks/solve", "trucks/solve"],
};

async function fetchFirstOk(paths, init) {
  for (const p of paths) {
    try {
      const base = API_BASE.replace(/\/$/, "");
      const res = await fetch(`${base}/${p}`, init);
      if (res.ok) return { res, path: p };
    } catch (_) {}
  }
  return null;
}

/**
 * Attempts SSE first (/<series>/solve_stream), falls back to POST solve endpoints if SSE isn't available.
 * `seriesKey` must be one of: "cup" | "xfinity" | "trucks".
 */
async function solveStream(seriesKey, payload, onItem, onDone) {
  const s = String(seriesKey || "cup");
  const postPaths = SERIES_POST_PATHS[s] || SERIES_POST_PATHS.cup;

  // 1) Try direct SSE route first
  let res;
  try {
    res = await fetch(`${API_BASE}/${s}/solve_stream`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...payload, series: s }),
    });
  } catch (_) {
    res = null;
  }

  // 2) If SSE not working, fall back to non-stream solve (or alternate paths)
  if (!res || !res.ok || !res.body) {
    const attempt = await fetchFirstOk([...postPaths, "solve"], {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...payload, series: s }),
    });
    if (!attempt) throw new Error(`Solve failed: No working ${s} endpoint found.`);

    const j = await attempt.res.json();
    (j.lineups || []).forEach((L) => onItem && onItem(L));
    onDone && onDone({ produced: (j.lineups || []).length, reason: "fallback-complete" });
    return;
  }

  // 3) Consume SSE stream
  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buf = "";
  let sawAny = false;

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buf += decoder.decode(value, { stream: true });

    // SSE messages are separated by double newlines
    const parts = buf.split("\n\n");
    buf = parts.pop();

    for (const part of parts) {
      const trimmed = part.trim();
      if (!trimmed) continue;

      let msg;
      try { msg = JSON.parse(trimmed); } catch { continue; }
      sawAny = true;
      if (msg.done) { onDone && onDone(msg); return; }
      onItem && onItem(msg);
    }
  }

  // 4) If stream ended without emitting anything, retry via POST
  if (!sawAny) {
    const attempt = await fetchFirstOk([...postPaths, "solve"], {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...payload, series: s }),
    });
    if (!attempt) throw new Error(`Solve failed: No working ${s} endpoint found.`);

    const j = await attempt.res.json();
    (j.lineups || []).forEach((L) => onItem && onItem(L));
    onDone && onDone({ produced: (j.lineups || []).length, reason: "fallback-complete" });
  } else {
    onDone && onDone({ produced: undefined, reason: "stream-ended-no-done" });
  }
}

/* --------- robust per-site build naming to avoid stale length ------- */
function nextBuildNameForSite(keyPrefix, site) {
  try {
    const raw = localStorage.getItem(`${keyPrefix}.${site}.builds`);
    const arr = raw ? JSON.parse(raw) : [];
    const nums = arr
      .map((b) => (b?.name ? String(b.name).match(/^Build\s+(\d+)$/i) : null))
      .filter(Boolean)
      .map((m) => Number(m[1]))
      .filter((n) => Number.isFinite(n));
    const next = nums.length ? Math.max(...nums) + 1 : 1;
    return `Build ${next}`;
  } catch {
    return "Build 1";
  }
}

function makeBuildId() {
  // Fixes “delete one build deletes both” (duplicate IDs in same ms).
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) {}
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/* ---------------------- UI: Driver multi-picker --------------------- */
function DriverMultiPicker({ allDrivers, value, onChange, placeholder = "Add drivers…", isDark }) {
  const [q, setQ] = React.useState("");
  const selected = React.useMemo(() => new Set(value), [value]);

  const suggestions = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return allDrivers.filter((n) => !selected.has(n) && n.toLowerCase().includes(needle)).slice(0, 12);
  }, [q, allDrivers, selected]);

  const add = (name) => {
    if (!selected.has(name)) onChange([...(value || []), name]);
    setQ("");
  };
  const remove = (name) => onChange((value || []).filter((v) => v !== name));

  return (
    <div className="w-full">
      <div
        className={
          "flex flex-wrap items-center gap-2 rounded-md px-2 py-1.5 border " +
          (isDark ? "bg-slate-900/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800")
        }
      >
        {(value || []).map((name) => (
          <span
            key={name}
            className={
              "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] border " +
              (isDark ? "bg-slate-800/90 border-slate-600 text-slate-100" : "bg-blue-50 border-blue-200 text-blue-800")
            }
          >
            {name}
            <button
              onClick={() => remove(name)}
              className={"leading-none text-xs hover:opacity-80 " + (isDark ? "text-slate-300" : "text-blue-600")}
              title="Remove"
              type="button"
            >
              ×
            </button>
          </span>
        ))}

        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && suggestions.length) {
              e.preventDefault();
              add(suggestions[0]);
            }
          }}
          className="flex-1 min-w-[160px] outline-none text-sm py-1 bg-transparent"
          placeholder={placeholder}
        />
      </div>

      {!!q && suggestions.length > 0 && (
        <div
          className={
            "mt-1 max-h-48 overflow-auto rounded-md border shadow-sm " +
            (isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200")
          }
        >
          {suggestions.map((n) => (
            <button
              key={n}
              className={
                "block w-full text-left px-3 py-1.5 text-sm " +
                (isDark ? "text-slate-100 hover:bg-slate-800/80" : "text-slate-900 hover:bg-blue-50")
              }
              onClick={() => add(n)}
              type="button"
            >
              {n}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const NASCAR_RULE_FIELDS = [
  ["drivers", "Specific drivers"], ["team", "Team"], ["manufacturer", "Manufacturer"],
  ["salary", "Salary"], ["qual", "Starting position"], ["proj", "Projection"],
  ["floor", "Floor"], ["ceil", "Ceiling"], ["pown", "pOWN %"], ["opt", "Opt %"],
];
const emptyNascarSelector = () => ({ type: "drivers", drivers: [], value: "", min: "", max: "" });
const packNascarSelector = (s) => {
  if (s.type === "drivers") return { type: s.type, drivers: s.drivers || [] };
  if (s.type === "team" || s.type === "manufacturer") return { type: s.type, value: s.value || "" };
  return { type: s.type, min: s.min === "" || s.min == null ? null : Number(s.min),
    max: s.max === "" || s.max == null ? null : Number(s.max) };
};
const nascarSelectorError = (s, rows) => {
  if (!s) return "Select a rule condition.";
  if (s.type === "drivers") return s.drivers?.length ? "" : "Select at least one driver.";
  if (s.type === "team" || s.type === "manufacturer")
    return !s.value || rows.some((r) => !r[s.type]) ? `Complete the ${s.type} data and selection.` : "";
  if (s.type === "qual" && rows.some((r) => r.qual == null)) return "Starting positions are not available for all drivers yet.";
  if ((s.min === "" || s.min == null) && (s.max === "" || s.max == null)) return "Enter a minimum or maximum.";
  if (s.min !== "" && s.max !== "" && Number(s.min) > Number(s.max)) return "Minimum exceeds maximum.";
  return "";
};

function NascarRuleSelector({ selector, onChange, allDrivers, teams, manufacturers, hasQual, isDark, inputClass }) {
  const s = selector || emptyNascarSelector();
  const options = s.type === "team" ? teams : manufacturers;
  const numeric = !["drivers", "team", "manufacturer"].includes(s.type);
  return (
    <div className="flex flex-wrap items-start gap-2">
      <select className={inputClass} value={s.type} onChange={(e) => onChange({ ...emptyNascarSelector(), type: e.target.value })}>
        {NASCAR_RULE_FIELDS.map(([value, label]) => (
          <option key={value} value={value} disabled={value === "qual" && !hasQual}>{label}</option>
        ))}
      </select>
      {s.type === "drivers" && <div className="min-w-[220px] flex-1">
        <DriverMultiPicker allDrivers={allDrivers} value={s.drivers || []}
          onChange={(drivers) => onChange({ ...s, drivers })} placeholder="Select drivers…" isDark={isDark} />
      </div>}
      {(s.type === "team" || s.type === "manufacturer") && <select className={inputClass} value={s.value || ""}
        onChange={(e) => onChange({ ...s, value: e.target.value })}>
        <option value="">Select {s.type}…</option>
        {options.map((v) => <option key={v} value={v}>{v}</option>)}
      </select>}
      {numeric && <>
        <input type="number" className={`${inputClass} w-28`} placeholder="Min" value={s.min ?? ""}
          onChange={(e) => onChange({ ...s, min: e.target.value })} />
        <span className="pt-1 text-xs opacity-70">to</span>
        <input type="number" className={`${inputClass} w-28`} placeholder="Max" value={s.max ?? ""}
          onChange={(e) => onChange({ ...s, max: e.target.value })} />
      </>}
    </div>
  );
}

/* ============================== page =============================== */
export default function NascarOptimizer() {
  const location = useLocation();
  const navigate = useNavigate();

  // Detect series from URL (public O'Reilly route maps to internal "xfinity" key)
  const routeSeries = useMemo(() => {
    const p = (location?.pathname || "").toLowerCase();

    if (
      p.includes("/nascar/oreilly/optimizer") ||
      p.includes("/nascar/xfinity/optimizer")
    ) {
      return "xfinity";
    }

    if (p.includes("/nascar/trucks/optimizer")) return "trucks";
    if (p.includes("/nascar/cup/optimizer")) return "cup";

    // fallback: look for /nascar/<series>/
    const m = p.match(/\/nascar\/(cup|oreilly|xfinity|trucks)\b/);
    if (m?.[1] === "oreilly") return "xfinity";
    return m?.[1] || "cup";
  }, [location?.pathname]);

  // Series switch (Cup / O'Reilly / Trucks) — persisted, but always aligned to route
  const [series, setSeries] = useStickyState("nascarOpt.series", "cup");
  useEffect(() => {
    if (routeSeries && routeSeries !== series) setSeries(routeSeries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeSeries]);

  const seriesCfg = SERIES[series] || SERIES.cup;

  const SOURCE = seriesCfg.projections;
  const SITE_IDS_SOURCE = seriesCfg.siteIds;

  const projMetaUrl = SOURCE.replace(/projections\.json$/, "meta.json");
  const idsMetaUrl = SITE_IDS_SOURCE.replace(/site_ids\.json$/, "meta.json");

  const { data, err, loading } = useJson(SOURCE);
  const { data: siteIds } = useJson(SITE_IDS_SOURCE);
  const { data: driverData } = useJson(seriesCfg.driverData);

  const projUpdated = useLastUpdated(SOURCE, projMetaUrl);
  const idsUpdated = useLastUpdated(SITE_IDS_SOURCE, idsMetaUrl);
  const updatedAt = useMemo(() => {
    if (projUpdated && idsUpdated) return new Date(Math.max(+projUpdated, +idsUpdated));
    return projUpdated || idsUpdated || null;
  }, [projUpdated, idsUpdated]);

  const keyPrefix = `nascarOpt.${series}`;
  const [theme, setTheme] = useStickyState(`${keyPrefix}.theme`, "dark");
  const isDark = theme === "dark";
  const T = isDark ? OPT_DARK_THEME : OPT_LIGHT_THEME;
  const panelClass = isDark
    ? "border border-slate-800 bg-slate-900/95 shadow-[0_20px_60px_rgba(15,23,42,0.9)]"
    : "border border-slate-200 bg-slate-50 shadow-sm";

  const [site, setSite] = useStickyState(`${keyPrefix}.site`, "dk");
  const cfg = SITES[site];

  /* ---- PERSISTED constraints (per-site) ---- */
  const buildsKey = (k) => `${keyPrefix}.${site}.${k}`;

  // ✅ FIX: make these per-site too (so DK/FD don't step on each other)
  const [optBy, setOptBy] = useStickyState(buildsKey("optBy"), "proj");
  const [numLineups, setNumLineups] = useStickyState(buildsKey("N"), 20);
  const [maxSalary, setMaxSalary] = useStickyState(buildsKey("cap"), cfg.cap);
  const [globalMax, setGlobalMax] = useStickyState(buildsKey("gmax"), 100);
  const [maxPown, setMaxPown] = useStickyState(buildsKey("maxPown"), ""); // per-driver max (0-100)
  const [maxLineupPown, setMaxLineupPown] = useStickyState(buildsKey("maxLineupPown"), ""); // ✅ NEW: lineup sum cap (0-600/500)
  const [maxGeoPown, setMaxGeoPown] = useStickyState(buildsKey("maxGeoPown"), ""); // geo mean cap (0-100)
  const [randomness, setRandomness] = useStickyState(buildsKey("rand"), 0);

  const [q, setQ] = useState("");

  const [locksArr, setLocksArr] = useStickyState(buildsKey("locks"), []);
  const [exclsArr, setExclsArr] = useStickyState(buildsKey("excls"), []);
  const locks = useMemo(() => new Set(Array.isArray(locksArr) ? locksArr : []), [locksArr]);
  const excls = useMemo(() => new Set(Array.isArray(exclsArr) ? exclsArr : []), [exclsArr]);

  const [minPct, setMinPct] = useStickyState(buildsKey("minPct"), {});
  const [maxPct, setMaxPct] = useStickyState(buildsKey("maxPct"), {});
  const [boost, setBoost] = useStickyState(buildsKey("boost"), {});
  const [customProj, setCustomProj] = useStickyState(buildsKey("customProj"), {});
  const [customProjDraft, setCustomProjDraft] = useState({});
  const [groups, setGroups] = useStickyState(buildsKey("groups"), []);
  const [pairCaps, setPairCaps] = useStickyState(buildsKey("pairCaps"), []);
  const [categoryRules, setCategoryRules] = useStickyState(buildsKey("categoryRules"), []);
  const [startingPositionRules, setStartingPositionRules] = useStickyState(buildsKey("startingPositionRules"), []);
  const [ifThenRules, setIfThenRules] = useStickyState(buildsKey("ifThenRules"), []);
  const [advancedOpen, setAdvancedOpen] = useStickyState(buildsKey("advancedOpen"), {
    groups: false, pairCaps: false, team: false, manufacturer: false, startingPosition: false, conditional: false,
  });

  // Per-site builds (chips restored)
  const [builds, setBuilds] = useStickyState(buildsKey("builds"), []);
  const [activeBuildId, setActiveBuildId] = useStickyState(buildsKey("active"), null);

  const [editingBuildId, setEditingBuildId] = useState(null);
  const [editingName, setEditingName] = useState("");

  const [lineups, setLineups] = useState([]);
  const [stopInfo, setStopInfo] = useState(null);
  const [selectedExposureDriver, setSelectedExposureDriver] = useState(null);
  const [showBuildAnalysis, setShowBuildAnalysis] = useState(false);

  const usagePct = useMemo(() => {
    if (!lineups.length) return {};
    const m = new Map();
    for (const L of lineups) for (const d of L.drivers) m.set(d, (m.get(d) || 0) + 1);
    const total = Math.max(1, lineups.length);
    const out = {};
    for (const [driver, cnt] of m.entries()) out[driver] = (cnt / total) * 100;
    return out;
  }, [lineups]);

  const [progressActual, setProgressActual] = useState(0);
  const [progressUI, setProgressUI] = useState(0);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const tickRef = useRef(null);

  useEffect(() => {
    if (!isOptimizing) return;
    clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setProgressUI((p) => {
        const N = Math.max(1, Number(numLineups) || 1);
        const target = progressActual;
        const ceiling = N;
        const next = Math.min(Math.max(p + 1, target), ceiling);
        return next;
      });
    }, 250);
    return () => clearInterval(tickRef.current);
  }, [isOptimizing, progressActual, numLineups]);

  // reset visuals on site/series change (keep constraints & builds persisted)
useEffect(() => {
  setLineups([]);
  setStopInfo(null);
  setProgressActual(0);
  setProgressUI(0);
  setIsOptimizing(false);
  setSelectedExposureDriver(null);
  setShowBuildAnalysis(false);
}, [site, series]);

  /* ------------------------------ rows ------------------------------ */
  const baseRows = useMemo(() => {
    if (!data) return [];

    const arr = Array.isArray(data?.rows) ? data.rows : Array.isArray(data) ? data : [];
    const rawDrivers = Array.isArray(driverData?.rows) ? driverData.rows : Array.isArray(driverData) ? driverData : [];
    const driverMeta = new Map(rawDrivers.map((r) => [normName(r?.Driver ?? r?.Driver_1), r]));

    const getPct = (obj, list) => {
      if (!obj) return 0;
      const k = Array.isArray(list) ? list.find((x) => obj?.[x] != null) : list;
      if (!k) return 0;
      const v = num(obj[k]);
      return v > 0 ? v / 100 : 0;
    };

    return arr
      .map((r) => {
        const driver = String(r?.Driver ?? r?.["Driver_1"] ?? r?.["DRIVER"] ?? "").trim();
        const meta = driverMeta.get(normName(driver)) || {};
        const qualRaw = r?.Qual ?? r?.qual ?? meta?.Qual ?? meta?.qual;
        const qual = qualRaw == null || String(qualRaw).trim() === "" ? null : Number(qualRaw);

        const salary = num(r?.[cfg.salary]);
        const proj = num(r?.[cfg.proj]);

        return {
          driver,
          qual: Number.isInteger(qual) && qual > 0 ? qual : null,
          team: String(r?.Team ?? r?.team ?? meta?.Team ?? "").trim(),
          manufacturer: String(r?.["Car Make"] ?? r?.manufacturer ?? meta?.["Car Make"] ?? "").trim(),
          salary,
          proj,
          baseProj: proj,
          floor: num(r?.[cfg.floor]),
          ceil: num(r?.[cfg.ceil]),
          pown: getPct(r, cfg.pown),
          opt: getPct(r, cfg.opt),
          val: salary > 0 ? proj / (salary / 1000) : 0,
        };
      })
      .filter((r) => r.driver && r.salary > 0);
  }, [data, driverData, cfg, site]);

  const rows = useMemo(() => {
    return baseRows.map((r) => {
      const rawCustom = customProj?.[r.driver];
      const hasCustomProj = rawCustom != null && String(rawCustom).trim() !== "";
      const baseProj = r.baseProj ?? r.proj ?? 0;
      const activeProj = hasCustomProj ? Math.max(0, num(rawCustom)) : baseProj;
      return {
        ...r,
        baseProj,
        proj: activeProj,
        customProj: rawCustom ?? "",
        hasCustomProj,
        val: r.salary > 0 ? activeProj / (r.salary / 1000) : 0,
      };
    });
  }, [baseRows, customProj]);

  // Map for rebuilding chosen on-the-fly (no longer stored in builds)
  const rowsByDriver = useMemo(() => new Map(rows.map((r) => [r.driver, r])), [rows]);

  /* ----------------------- filter + stable order --------------------- */
  const [order, setOrder] = useState([]);
  const sortRef = useRef({ col: "proj", dir: "desc" });

  useEffect(() => {
    const initial = [...rows].sort((a, b) => {
      // Primary: Projection
      if ((a.proj || 0) !== (b.proj || 0)) {
        return (b.proj || 0) - (a.proj || 0);
      }

      // If both projections are 0 → sort by salary desc
      if ((a.proj || 0) === 0 && (b.proj || 0) === 0) {
        if ((a.salary || 0) !== (b.salary || 0)) {
          return (b.salary || 0) - (a.salary || 0);
        }
      }

      // Final tiebreaker: alphabetical
      return a.driver.localeCompare(b.driver);
    });

    setOrder(initial.map((r) => r.driver));
  }, [site, rows.length]);

  const boostedProj = (r) => r.proj * (1 + 0.03 * (boost[r.driver] || 0));

  const displayRows = useMemo(() => {
    const byName = new Map(rows.map((r) => [r.driver, r]));
    const ordered = order.map((n) => byName.get(n)).filter(Boolean);
    const others = rows.filter((r) => !order.includes(r.driver));
    const base = [...ordered, ...others];

    const needle = q.trim().toLowerCase();
    if (!needle) return base;

    return base.filter(
      (r) =>
        r.driver.toLowerCase().includes(needle) ||
        r.team.toLowerCase().includes(needle) ||
        r.manufacturer.toLowerCase().includes(needle) ||
        String(r.qual).includes(needle) ||
        String(r.salary).includes(needle) ||
        `${usagePct[r.driver] ?? ""}`.includes(needle)
    );
  }, [rows, order, q, usagePct, boost]);

  const sortable = new Set(["qual", "team", "manufacturer", "salary", "proj", "val", "floor", "ceil", "pown", "opt", "usage"]);

  const setSort = (col) => {
    if (!sortable.has(col)) return;

    const dir = sortRef.current.col === col ? (sortRef.current.dir === "asc" ? "desc" : "asc") : "desc";
    sortRef.current = { col, dir };
    const mult = dir === "asc" ? 1 : -1;

    const sorted = [...displayRows].sort((a, b) => {
      const getVal = (r) => {
        if (col === "usage") return usagePct[r.driver] ?? -Infinity;
        if (col === "pown" || col === "opt") return (r[col] || 0) * 100;
        if (col === "proj") return boostedProj(r);
        if (col === "val") {
          const salK = (r.salary || 0) / 1000;
          return salK > 0 ? boostedProj(r) / salK : -Infinity;
        }
        return r[col] ?? 0;
      };
      const va = getVal(a);
      const vb = getVal(b);

      if (col === "team" || col === "manufacturer")
        return String(va).localeCompare(String(vb)) * mult || a.driver.localeCompare(b.driver);

      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return a.driver.localeCompare(b.driver) * mult;
    });

    setOrder(sorted.map((r) => r.driver));
  };

  const sortArrow = (key) => (sortRef.current.col === key ? (sortRef.current.dir === "asc" ? " ▲" : " ▼") : "");

  /* ----------------------------- actions ---------------------------- */
  const bumpBoost = (d, step) => setBoost((m) => ({ ...m, [d]: clamp((m[d] || 0) + step, -6, 6) }));

  const setDriverCustomProj = (driver, value) => {
    setCustomProj((m) => {
      const next = { ...(m || {}) };
      if (value == null || String(value).trim() === "") delete next[driver];
      else next[driver] = String(value);
      return next;
    });
  };

  const resetDriverCustomProj = (driver) => {
    setCustomProj((m) => {
      const next = { ...(m || {}) };
      delete next[driver];
      return next;
    });
    setCustomProjDraft((m) => {
      const next = { ...(m || {}) };
      delete next[driver];
      return next;
    });
  };

  const toggleLock = (d) =>
    setLocksArr((arr) => {
      const has = (arr || []).includes(d);
      return has ? (arr || []).filter((x) => x !== d) : [...(arr || []), d];
    });

  const toggleExcl = (d) =>
    setExclsArr((arr) => {
      const has = (arr || []).includes(d);
      return has ? (arr || []).filter((x) => x !== d) : [...(arr || []), d];
    });

  const resetExposuresOnly = () => {
    setMinPct({});
    setMaxPct({});
  };

  const resetBoostsOnly = () => {
  setBoost({});
};

  const resetConstraints = () => {
    setLocksArr([]);
    setExclsArr([]);
    setMinPct({});
    setMaxPct({});
    setBoost({});
    setCustomProj({});
    setCustomProjDraft({});
    setGroups([]);
    setPairCaps([]);
    setCategoryRules([]);
    setStartingPositionRules([]);
    setIfThenRules([]);
  };

  const resetEverything = () => {
    setLocksArr([]);
    setExclsArr([]);

    setMinPct({});
    setMaxPct({});
    setBoost({});
    setCustomProj({});
    setCustomProjDraft({});
    setGroups([]);
    setPairCaps([]);
    setCategoryRules([]);
    setStartingPositionRules([]);
    setIfThenRules([]);
    setAdvancedOpen({ groups: false, pairCaps: false, team: false, manufacturer: false, startingPosition: false, conditional: false });

    setOptBy("proj");
    setNumLineups(20);
    setMaxSalary(cfg.cap);
    setGlobalMax(100);
    setMaxPown("");
    setMaxLineupPown("");
    setMaxGeoPown("");
    setRandomness(0);
    setQ("");

    setBuilds([]);
    setActiveBuildId(null);
    setLineups([]);
    setStopInfo(null);
    setSelectedExposureDriver(null);
    setShowBuildAnalysis(false);

    setProgressActual(0);
    setProgressUI(0);
    setIsOptimizing(false);
  };

  /* --------------------------- builds ---------------------------- */
  function loadBuild(id) {
    const b = (Array.isArray(builds) ? builds : []).find((x) => x.id === id);
    if (!b) return;

    setActiveBuildId(id);

    const Ls = Array.isArray(b.lineups) ? b.lineups : [];
    setLineups(Ls);

    setProgressActual(Ls.length);
    setProgressUI(Ls.length);
    setStopInfo(null);
  }

  function saveBuild(name, data) {
    const id = makeBuildId();

    const minimalLineups = (data || []).map((L) => ({
      drivers: Array.isArray(L.drivers) ? L.drivers : [],
      salary: Number(L.salary) || 0,
      total: Number(L.total) || 0,
    }));

    const rec = {
      id,
      name,
      site,
      ts: new Date().toISOString(),
      settings: {
        site,
        optBy,
        numLineups: Math.max(1, Number(numLineups) || 1),
        cap: Math.min(cfg.cap, Number(maxSalary) || cfg.cap),
        globalMax,
        maxPown,
        maxLineupPown,
        maxGeoPown,
        randomness,
        locks: Array.isArray(locksArr) ? locksArr : [],
        excls: Array.isArray(exclsArr) ? exclsArr : [],
        minPct,
        maxPct,
        boost,
        groups,
        pairCaps,
        categoryRules,
        ifThenRules,
      },
      lineups: minimalLineups,
    };

    const next = [...(Array.isArray(builds) ? builds : []), rec];
    setBuilds(next);
    setActiveBuildId(id);
    setLineups(minimalLineups); // show immediately
    setProgressActual(minimalLineups.length);
    setProgressUI(minimalLineups.length);
  }

  function renameBuild(id, newName) {
    setBuilds((B) => (Array.isArray(B) ? B.map((b) => (b.id === id ? { ...b, name: newName || b.name } : b)) : B));
  }

  function deleteBuild(id) {
    setBuilds((B) => {
      const arr = Array.isArray(B) ? B : [];
      const next = arr.filter((b) => b.id !== id);

      // if deleting active, auto-load last remaining (so UI never goes “blank” unless none)
      if (activeBuildId === id) {
        const fallback = next.length ? next[next.length - 1].id : null;
        setActiveBuildId(fallback);
        if (fallback) {
          const fb = next.find((x) => x.id === fallback);
          const Ls = Array.isArray(fb?.lineups) ? fb.lineups : [];
          setLineups(Ls);
          setProgressActual(Ls.length);
          setProgressUI(Ls.length);
        } else {
          setLineups([]);
          setProgressActual(0);
          setProgressUI(0);
        }
      }

      return next;
    });
  }

  // ✅ Auto-show builds after refresh / navigation (load active or last build)
  const didHydrateRef = useRef(false);
  useEffect(() => {
    const arr = Array.isArray(builds) ? builds : [];
    if (!arr.length) {
      didHydrateRef.current = true;
      return;
    }

    // If we already have lineups, don't fight the UI.
    if (lineups.length) {
      didHydrateRef.current = true;
      return;
    }

    // On first load for this site/series, hydrate from active or last
    if (!didHydrateRef.current) {
      const targetId = activeBuildId || arr[arr.length - 1].id;
      if (targetId && targetId !== activeBuildId) setActiveBuildId(targetId);
      const b = arr.find((x) => x.id === targetId) || arr[arr.length - 1];
      const Ls = Array.isArray(b?.lineups) ? b.lineups : [];
      setLineups(Ls);
      setProgressActual(Ls.length);
      setProgressUI(Ls.length);
      didHydrateRef.current = true;
    } else {
      // After first hydrate, if activeBuildId exists and lineups empty (e.g. route/site switch reset), load it.
      const targetId = activeBuildId || arr[arr.length - 1].id;
      const b = arr.find((x) => x.id === targetId);
      if (b) {
        const Ls = Array.isArray(b?.lineups) ? b.lineups : [];
        setLineups(Ls);
        setProgressActual(Ls.length);
        setProgressUI(Ls.length);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, site, buildsKey("builds")]);

  // when activeBuildId changes (user clicks a chip), load it
  useEffect(() => {
    const arr = Array.isArray(builds) ? builds : [];
    if (!arr.length) return;
    if (!activeBuildId) return;
    const b = arr.find((x) => x.id === activeBuildId);
    if (!b) return;
    const Ls = Array.isArray(b.lineups) ? b.lineups : [];
    setLineups(Ls);
    setProgressActual(Ls.length);
    setProgressUI(Ls.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBuildId]);

  /* --------------------------- optimize (SSE) ------------------------ */
  async function optimize() {
    if (!rows.length) return;
    if (startingPositionRules.length && !hasQual) {
      alert("Starting-position rules require valid starting positions for every driver.");
      return;
    }
    const isWholeNumber = (v, min, max = Infinity) =>
      v !== "" && v != null && Number.isInteger(Number(v)) && Number(v) >= min && Number(v) <= max;
    for (const [i, rule] of startingPositionRules.entries()) {
      if (!isWholeNumber(rule.start_min, 1) || !isWholeNumber(rule.start_max, 1) || Number(rule.start_min) > Number(rule.start_max)) {
        alert(`Starting-position rule ${i + 1}: enter a valid inclusive position range.`);
        return;
      }
      if (!["between", "at_least", "at_most", "exactly"].includes(rule.mode)) {
        alert(`Starting-position rule ${i + 1}: select a count condition.`);
        return;
      }
      const counts = rule.mode === "between" ? [rule.min_count, rule.max_count] : [rule.count];
      if (counts.some((v) => !isWholeNumber(v, 0, cfg.roster)) ||
          (rule.mode === "between" && Number(rule.min_count) > Number(rule.max_count))) {
        alert(`Starting-position rule ${i + 1}: driver counts must be whole numbers from 0 to ${cfg.roster}, with minimum no greater than maximum.`);
        return;
      }
    }
    for (const rule of categoryRules || []) {
      if (!rule.value || rows.some((r) => !r[rule.field])) {
        alert(`Complete the ${rule.field} data and rule before optimizing.`);
        return;
      }
    }
    for (const rule of ifThenRules || []) {
      const issue = nascarSelectorError(rule.if_selector, rows) || nascarSelectorError(rule.then_selector, rows);
      if (issue) { alert(issue); return; }
    }

  setLineups([]);
  setStopInfo(null);
  setProgressActual(0);
  setProgressUI(0);
  setIsOptimizing(true);
  setSelectedExposureDriver(null);

    const N = Math.max(1, Number(numLineups) || 1);

    const lineupPownHi = cfg.roster * 100; // DK=600, FD=500
    const maxLineupPownClamped =
      maxLineupPown === "" || maxLineupPown == null
        ? null
        : clamp(Number(maxLineupPown) || 0, 0, lineupPownHi);

    const payload = {
      players: rows.map((r) => ({
        driver: r.driver,
        salary: Math.round(r.salary || 0),
        proj: r.proj || 0,
        floor: r.floor || 0,
        ceil: r.ceil || 0,
        pown: r.pown || 0,
        opt: r.opt || 0,
        ...(categoryRules.length || ifThenRules.length || startingPositionRules.length ? {
          team: r.team || null, manufacturer: r.manufacturer || null, qual: r.qual,
        } : {}),
      })),
      roster: cfg.roster,
      cap: Math.min(cfg.cap, Number(maxSalary) || cfg.cap),
      n: N,
      objective: optBy,
      locks: Array.isArray(locksArr) ? locksArr : [],
      excludes: Array.isArray(exclsArr) ? exclsArr : [],
      boosts: boost,
      randomness: clamp(Number(randomness) || 0, 0, 100),
      global_max_pct: clamp(Number(globalMax) || 100, 0, 100),

      max_player_pown: maxPown === "" || maxPown == null ? null : clamp(Number(maxPown) || 0, 0, 100),
      lineup_pown_max: maxLineupPownClamped,
      geo_mean_pown_max: maxGeoPown === "" || maxGeoPown == null ? null : clamp(Number(maxGeoPown) || 0, 0, 100),

      min_pct: Object.fromEntries(Object.entries(minPct).map(([k, v]) => [k, clamp(Number(v) || 0, 0, 100)])),
      max_pct: Object.fromEntries(Object.entries(maxPct).map(([k, v]) => [k, clamp(Number(v) || 100, 0, 100)])),
      min_diff: 1,
      time_limit_ms: 1500,
      groups: (groups || []).map((g) => ({
        mode: g.mode || "at_most",
        count: Math.max(0, Number(g.count) || 0),
        players: Array.isArray(g.players) ? g.players : [],
      })),

      pair_caps: (pairCaps || [])
        .map((p) => {
          const players = Array.isArray(p.players) ? p.players.filter(Boolean).slice(0, 2) : [];
          return {
            players: [...players].sort((a, b) => a.localeCompare(b)),
            max_pct: clamp(Number(p.maxPct) || 0, 0, 100),
          };
        })
        .filter((p) => p.players.length === 2),
      ...(startingPositionRules.length ? { starting_position_rules: startingPositionRules.map((r) => ({
        start_min: Number(r.start_min), start_max: Number(r.start_max), mode: r.mode,
        ...(r.mode === "between"
          ? { min_count: Number(r.min_count), max_count: Number(r.max_count) }
          : { count: Number(r.count) }),
      })) } : {}),
      ...(categoryRules.length ? { category_rules: categoryRules.map((r) => ({
        field: r.field, value: r.value, mode: r.mode || "at_most", count: Math.max(0, Number(r.count) || 0),
      })) } : {}),
      ...(ifThenRules.length ? { if_then_rules: ifThenRules.map((r) => ({
        if_selector: packNascarSelector(r.if_selector), if_mode: r.if_mode || "at_least",
        if_count: Math.max(0, Number(r.if_count) || 0),
        then_selector: packNascarSelector(r.then_selector), then_mode: r.then_mode || "at_least",
        then_count: Math.max(0, Number(r.then_count) || 0),
      })) } : {}),
    };

    const out = [];
    try {
      await solveStream(
        series,
        payload,
        (evt) => {
          const L = { drivers: evt.drivers, salary: evt.salary, total: evt.total };
          out.push(L);
          setLineups((prev) => [...prev, L]);
          setProgressActual(out.length);
        },
        (done) => {
          if (done?.reason) setStopInfo(done);
          setProgressActual(out.length || payload.n);
          setProgressUI(out.length || payload.n);
          setIsOptimizing(false);
          clearInterval(tickRef.current);
          saveBuild(nextBuildNameForSite(keyPrefix, site), out);
        }
      );
    } catch (e) {
      const postPaths = SERIES_POST_PATHS[series] || SERIES_POST_PATHS.cup;
      const attempt = await fetchFirstOk([...postPaths, "solve"], {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payload, series }),
      });

      if (!attempt) {
        alert(`Solve failed: No working ${series} endpoint found.`);
        setIsOptimizing(false);
        clearInterval(tickRef.current);
        return;
      }

      const j = await attempt.res.json();
      const out2 =
        (j.lineups || []).map((L) => ({
          drivers: L.drivers,
          salary: L.salary,
          total: L.total,
        })) || [];

      setLineups(out2);
      setProgressActual(out2.length);
      setProgressUI(out2.length);
      setIsOptimizing(false);
      clearInterval(tickRef.current);

      if ((j.produced || 0) < payload.n)
        setStopInfo({ produced: j.produced || 0, requested: payload.n, reason: "stopped_early" });
      saveBuild(nextBuildNameForSite(keyPrefix, site), out2);
    }
  }

  /* ------------------------------- UI -------------------------------- */
  const metricLabel =
    optBy === "proj"
      ? "Proj"
      : optBy === "floor"
      ? "Floor"
      : optBy === "ceil"
      ? "Ceiling"
      : optBy === "pown"
      ? "pOWN%"
      : "Opt%";

  const cell = "px-2 py-1 text-center";
  const header = "px-2 py-1 font-semibold text-center";
  const textSz = "text-[12px]";

  const TABLE_COLS = [
    { key: "lock", label: "Lock" },
    { key: "excl", label: "Excl" },
    { key: "qual", label: "Qual", sortable: true },
    { key: "boosts", label: "Boosts" },
    { key: "driver", label: "Driver", sortable: false },
    { key: "team", label: "Team", sortable: true },
    { key: "manufacturer", label: "Manufacturer", sortable: true },
    { key: "salary", label: "Salary", sortable: true },
    { key: "proj", label: "Proj", sortable: true },
    { key: "val", label: "Val", sortable: true },
    { key: "floor", label: "Floor", sortable: true },
    { key: "ceil", label: "Ceiling", sortable: true },
    { key: "pown", label: "pOWN%", sortable: true },
    { key: "opt", label: "Opt%", sortable: true },
    { key: "min", label: "Min%" },
    { key: "max", label: "Max%" },
    { key: "usage", label: "Usage%", sortable: true },
  ];

  const allDriverNames = useMemo(() => rows.map((r) => r.driver), [rows]);
  const teamOptions = useMemo(() => [...new Set(rows.map((r) => r.team).filter(Boolean))].sort(), [rows]);
  const manufacturerOptions = useMemo(() => [...new Set(rows.map((r) => r.manufacturer).filter(Boolean))].sort(), [rows]);
  const hasQual = rows.length > 0 && rows.every((r) => r.qual != null);

  const getChosenForLineup = (L) => {
    const names = Array.isArray(L?.drivers) ? L.drivers : [];
    return names.map((n) => rowsByDriver.get(n)).filter(Boolean);
  };

  const selectedDriverPairings = useMemo(() => {
  if (!selectedExposureDriver || !lineups.length) return [];

  const counts = new Map();
  let lineupCount = 0;

  for (const L of lineups) {
    const drivers = Array.isArray(L.drivers) ? L.drivers : [];
    if (!drivers.includes(selectedExposureDriver)) continue;

    lineupCount += 1;

    for (const d of drivers) {
      if (d === selectedExposureDriver) continue;
      counts.set(d, (counts.get(d) || 0) + 1);
    }
  }

  return [...counts.entries()]
    .map(([driver, count]) => ({
      driver,
      count,
      pct: lineupCount > 0 ? (count / lineupCount) * 100 : 0,
    }))
    .sort((a, b) => b.count - a.count || b.pct - a.pct || a.driver.localeCompare(b.driver));
}, [selectedExposureDriver, lineups]);

  const buildsArr = Array.isArray(builds) ? builds : [];

  const buildAnalysis = useMemo(() => {
  if (!lineups.length) {
    return {
      avgSalaryUsed: 0,
      avgSalaryRemaining: 0,
      avgProjection: 0,
      avgLineupPown: 0,
      avgGeoMeanPown: 0,
      avgStartingPosition: 0,
      topDrivers: [],
      topPairs: [],
      topTriples: [],
    };
  }

  const pairCounts = new Map();
  const tripleCounts = new Map();
  const driverCounts = new Map();

  let totalSalary = 0;
  let totalProjection = 0;
  let totalLineupPown = 0;
  let totalGeoMeanPown = 0;
  let totalAvgStart = 0;

  const makeCombos = (arr, size) => {
    const out = [];
    const n = arr.length;

    if (size === 2) {
      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          out.push([arr[i], arr[j]]);
        }
      }
    } else if (size === 3) {
      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          for (let k = j + 1; k < n; k++) {
            out.push([arr[i], arr[j], arr[k]]);
          }
        }
      }
    }

    return out;
  };

  for (const L of lineups) {
    const chosen = getChosenForLineup(L);
    const chosenSortedNames = chosen
      .map((r) => r.driver)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));

    const lineupPown = chosen.reduce((acc, r) => acc + (r.pown || 0) * 100, 0);
    const geoPown = geoMeanPct(chosen, (r) => (r.pown || 0) * 100);

    const qualVals = chosen
      .map((r) => Number(r.qual))
      .filter((v) => Number.isFinite(v) && v > 0);

    const avgStartThisLineup = qualVals.length
      ? qualVals.reduce((a, b) => a + b, 0) / qualVals.length
      : 0;

    totalSalary += Number(L.salary) || 0;
    totalProjection += Number(L.total) || 0;
    totalLineupPown += lineupPown;
    totalGeoMeanPown += geoPown;
    totalAvgStart += avgStartThisLineup;

    for (const d of chosenSortedNames) {
      driverCounts.set(d, (driverCounts.get(d) || 0) + 1);
    }

    for (const combo of makeCombos(chosenSortedNames, 2)) {
      const key = combo.join(" | ");
      pairCounts.set(key, (pairCounts.get(key) || 0) + 1);
    }

    for (const combo of makeCombos(chosenSortedNames, 3)) {
      const key = combo.join(" | ");
      tripleCounts.set(key, (tripleCounts.get(key) || 0) + 1);
    }
  }

  const lineupCount = Math.max(1, lineups.length);

  const topDrivers = [...driverCounts.entries()]
    .map(([driver, count]) => ({
      driver,
      count,
      pct: (count / lineupCount) * 100,
    }))
    .sort((a, b) => b.count - a.count || b.pct - a.pct || a.driver.localeCompare(b.driver))
    .slice(0, 12);

  const topPairs = [...pairCounts.entries()]
    .map(([key, count]) => {
      const drivers = key.split(" | ");
      return {
        drivers,
        label: drivers.join(" • "),
        count,
        pct: (count / lineupCount) * 100,
      };
    })
    .sort((a, b) => b.count - a.count || b.pct - a.pct || a.label.localeCompare(b.label))
    .slice(0, 12);

  const topTriples = [...tripleCounts.entries()]
    .map(([key, count]) => {
      const drivers = key.split(" | ");
      return {
        drivers,
        label: drivers.join(" • "),
        count,
        pct: (count / lineupCount) * 100,
      };
    })
    .sort((a, b) => b.count - a.count || b.pct - a.pct || a.label.localeCompare(b.label))
    .slice(0, 12);

  return {
    avgSalaryUsed: totalSalary / lineupCount,
    avgSalaryRemaining: cfg.cap - totalSalary / lineupCount,
    avgProjection: totalProjection / lineupCount,
    avgLineupPown: totalLineupPown / lineupCount,
    avgGeoMeanPown: totalGeoMeanPown / lineupCount,
    avgStartingPosition: totalAvgStart / lineupCount,
    topDrivers,
    topPairs,
    topTriples,
  };
}, [lineups, cfg.cap, getChosenForLineup]);

  // --- Cards sorting (like NFL) ---
  const [cardsSortBy, setCardsSortBy] = useStickyState(`${keyPrefix}.${site}.cardsSortBy`, "proj");
  const [cardsSortDir, setCardsSortDir] = useStickyState(`${keyPrefix}.${site}.cardsSortDir`, "desc");

  const lineupsForCards = useMemo(() => {
    const dir = cardsSortDir === "asc" ? 1 : -1;

  const getKey = (L) => {
    const chosen = getChosenForLineup(L);
    const chosenSorted = chosen.length ? [...chosen].sort((a, b) => (b?.salary || 0) - (a?.salary || 0)) : chosen;

    const totalPownPct = chosenSorted.reduce((acc, r) => acc + (r.pown || 0) * 100, 0);
    const geoMeanPownPct = geoMeanPct(chosenSorted, (r) => (r.pown || 0) * 100);
    const totalOptPct = chosenSorted.reduce((acc, r) => acc + (r.opt || 0) * 100, 0);

    if (cardsSortBy === "salary") return Number(L.salary) || 0;
    if (cardsSortBy === "pown") return totalPownPct || 0;
    if (cardsSortBy === "geo") return geoMeanPownPct || 0;
    if (cardsSortBy === "opt") return totalOptPct || 0;

    return Number(L.total) || 0;
  };

    return [...(lineups || [])].sort((a, b) => {
      const ka = getKey(a);
      const kb = getKey(b);
      if (ka < kb) return -1 * dir;
      if (ka > kb) return 1 * dir;
      return 0;
    });
  }, [lineups, cardsSortBy, cardsSortDir]);

  return (
    <div className={`${T.page} pb-8`}>
      <div className="w-full px-4 md:px-6 py-5">
        <header className="mb-3 space-y-3">
          <div className="flex flex-wrap lg:flex-nowrap items-center justify-between gap-x-5 gap-y-2">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className={`text-2xl font-extrabold md:text-3xl ${T.headerText}`}>
              {`NASCAR ${seriesCfg.label} — Optimizer`}
            </h1>
              {updatedAt && <span className={`text-xs italic ${T.subtext}`}>
                Updated: {fmtUpdated(updatedAt)}
              </span>}
            </div>
            <div className="flex w-full min-w-0 flex-wrap items-center gap-3 sm:w-auto sm:shrink-0">

              <div className="flex flex-wrap items-center gap-3">
              <div role="group" aria-label="Color theme"
                className={"inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " + T.pill}>
                <button type="button" aria-pressed={isDark} onClick={() => setTheme("dark")}
                  className={"rounded-full px-3 py-1 font-medium transition " + (isDark ? T.pillDarkBtnActive : T.pillDarkBtnIdle)}>Dark</button>
                <button type="button" aria-pressed={!isDark} onClick={() => setTheme("light")}
                  className={"rounded-full px-3 py-1 font-medium transition " + (!isDark ? T.pillLightBtnActive : T.pillLightBtnIdle)}>Light</button>
              </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className={`text-xs ${T.subtext}`}>Click here if new slate →</span>
                  <button type="button" onClick={resetEverything}
                    className="h-8 px-4 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700 active:bg-red-800">
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>

          <section aria-label="Site and series" className={`rounded-2xl p-3 ${panelClass}`}>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 lg:flex-nowrap">
            <div role="tablist" aria-label="Fantasy site" className={"inline-flex shrink-0 items-center gap-6 border-b " +
              (isDark ? "border-slate-800" : "border-slate-200")} >
              {["dk", "fd"].map((s) => {
                const selected = site === s;
                return (
                  <button key={s} type="button" role="tab" aria-selected={selected}
                    onClick={() => setSite(s)}
                    className={
                      "relative inline-flex h-9 items-center gap-2 whitespace-nowrap pb-2 text-sm font-semibold transition-colors " +
                      (selected
                        ? (isDark ? "text-white" : "text-slate-950") + " after:absolute after:bottom-[-1px] after:left-0 after:right-0 after:h-0.5 after:rounded-full after:bg-sky-400"
                        : isDark ? "text-slate-400 hover:text-slate-100" : "text-slate-500 hover:text-slate-900")
                    }
                  >
                    <img src={SITES[s].logo} alt="" className={isDark ? "h-4 w-4 opacity-90" : "h-4 w-4"} />
                    <span>{SITES[s].label}</span>
                  </button>
                );
              })}
            </div>

            <nav aria-label="NASCAR series" className={"inline-flex shrink-0 items-center gap-6 border-b " +
              (isDark ? "border-slate-800" : "border-slate-200")}>
              {Object.keys(SERIES).map((k) => (
                <button
                  key={k}
                  type="button"
                  aria-current={series === k ? "page" : undefined}
                  onClick={() => {
                    setSeries(k);
                    const to = SERIES[k]?.route || `/nascar/${k}/optimizer`;
                    if ((location?.pathname || "") !== to) navigate(to);
                  }}
                  className={"relative inline-flex h-9 items-center whitespace-nowrap pb-2 text-sm font-semibold transition-colors " +
                    (series === k
                      ? (isDark ? "text-white" : "text-slate-950") + " after:absolute after:bottom-[-1px] after:left-0 after:right-0 after:h-0.5 after:rounded-full after:bg-sky-400"
                      : isDark ? "text-slate-400 hover:text-slate-100" : "text-slate-500 hover:text-slate-900")}
                  title={`Switch to ${SERIES[k].label}`}
                >{SERIES[k].label}</button>
              ))}
            </nav>
            </div>
          </section>
        </header>

        {/* controls bar */}
        <section className={`rounded-2xl p-3 mb-4 ${panelClass}`} aria-label="Build settings">
          <h2 className="text-sm font-semibold mb-3">Build Settings</h2>
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-10">
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Optimize by</label>
            <select className={`w-full border rounded-md px-2 py-1.5 text-sm ${T.input}`} value={optBy} onChange={(e) => setOptBy(e.target.value)}>
              <option value="proj">Projection</option>
              <option value="floor">Floor</option>
              <option value="ceil">Ceiling</option>
              <option value="pown">pOWN%</option>
              <option value="opt">Opt%</option>
            </select>
          </div>

          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Lineups</label>
            <input className={`w-full border rounded-md px-2 py-1.5 text-sm ${T.input}`} value={numLineups} onChange={(e) => setNumLineups(e.target.value)} />
          </div>

          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Max salary</label>
            <input className={`w-full border rounded-md px-2 py-1.5 text-sm ${T.input}`} value={maxSalary} onChange={(e) => setMaxSalary(e.target.value)} />
          </div>

          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Global Max %</label>
            <input className={`w-full border rounded-md px-2 py-1.5 text-sm ${T.input}`} value={globalMax} onChange={(e) => setGlobalMax(e.target.value)} />
          </div>

          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Max pOWN % (D)</label>
            <input className={`w-full border rounded-md px-2 py-1.5 text-sm ${T.input}`} value={maxPown} onChange={(e) => setMaxPown(e.target.value)} />
          </div>

          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Max lineup pOWN</label>
            <input
              className={`w-full border rounded-md px-2 py-1.5 text-sm ${T.input}`}
              value={maxLineupPown}
              onChange={(e) => setMaxLineupPown(e.target.value)}
              placeholder={site === "dk" ? "0–600" : "0–500"}
              title={site === "dk" ? "Sum of lineup pOWN% (max 600)" : "Sum of lineup pOWN% (max 500)"}
            />
          </div>

          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Max geo mean pOWN%</label>
            <input
              className={`w-full border rounded-md px-2 py-1.5 text-sm ${T.input}`}
              value={maxGeoPown}
              onChange={(e) => setMaxGeoPown(e.target.value)}
              placeholder="e.g. 25–35"
              title="Caps geometric mean of lineup pOWN% (prevents all-chalk lineups)"
            />
          </div>

          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Randomness %</label>
            <input className={`w-full border rounded-md px-2 py-1.5 text-sm ${T.input}`} value={randomness} onChange={(e) => setRandomness(e.target.value)} />
          </div>

        </div>
        </section>
      </div>

      {/* Keep advanced build rules together; collapsed by default. */}
      <div className="w-full px-4 md:px-6 mb-4">
        <details className={`rounded-2xl p-3 ${panelClass}`}>
          <summary className="cursor-pointer select-none text-sm font-semibold">
            Build Rules
          </summary>
          <div className="mt-3">
      {/* NASCAR metadata and conditional rules. All sections start collapsed. */}
      <div className="w-full">
        {[["team", "Team Rules", teamOptions], ["manufacturer", "Manufacturer Rules", manufacturerOptions]].map(([field, label, options]) => {
          const active = (categoryRules || []).filter((r) => r.field === field);
          return <details key={field} className={`rounded-lg p-3 mb-2 ${T.card}`}
            open={!!advancedOpen?.[field]}
            onToggle={(e) => { const open = e.currentTarget.open; setAdvancedOpen((v) => ({ ...v, [field]: open })); }}>
            <summary className="cursor-pointer select-none text-sm font-semibold">
              {label} {active.length > 0 && <span className="ml-2 text-xs text-sky-400">{active.length}</span>}
            </summary>
            <div className="flex justify-end mt-3 mb-2">
              <button type="button" className={`border rounded-md px-3 py-1 text-sm ${T.input}`}
                onClick={() => setCategoryRules((old) => [...(old || []), { field, value: "", mode: "at_most", count: 2 }])}>
                + Add {field} rule
              </button>
            </div>
            {!options.length && <p className="text-xs text-amber-500 mb-2">No {field} data for this race. Check data.json.</p>}
            {active.map((r) => {
              const idx = categoryRules.indexOf(r);
              const change = (patch) => setCategoryRules((old) => old.map((x, i) => i === idx ? { ...x, ...patch } : x));
              return <div key={idx} className="flex flex-wrap gap-2 items-center border rounded-md p-2 mb-2">
                <span className="text-sm">Lineup has</span>
                <select className={`border rounded-md px-2 py-1 text-sm ${T.input}`} value={r.mode}
                  onChange={(e) => change({ mode: e.target.value })}>
                  <option value="at_most">at most</option><option value="at_least">at least</option><option value="exactly">exactly</option>
                </select>
                <input type="number" min="0" max={cfg.roster} className={`w-16 border rounded-md px-2 py-1 text-sm ${T.input}`}
                  value={r.count} onChange={(e) => change({ count: e.target.value })} />
                <span className="text-sm">drivers from</span>
                <select className={`border rounded-md px-2 py-1 text-sm ${T.input}`} value={r.value}
                  onChange={(e) => change({ value: e.target.value })}>
                  <option value="">Select {field}…</option>
                  <option value="*">Every {field} separately</option>
                  {options.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
                <button type="button" className="ml-auto text-sm px-2 py-1 border rounded-md" onClick={() => setCategoryRules((old) => old.filter((_, i) => i !== idx))}>Remove</button>
              </div>;
            })}
          </details>;
        })}

        <details className={`rounded-lg p-3 mb-2 ${T.card}`} open={!!advancedOpen?.startingPosition}
          onToggle={(e) => { const open = e.currentTarget.open; setAdvancedOpen((v) => ({ ...v, startingPosition: open })); }}>
          <summary className="cursor-pointer select-none text-sm font-semibold">Starting Position Rules
            {startingPositionRules.length > 0 && <span className="ml-2 text-xs text-sky-400">{startingPositionRules.length}</span>}
          </summary>
          <p className="text-xs text-slate-400 mt-3">Count drivers starting within each inclusive position range in every lineup. All rules apply together; overlapping ranges count the same driver in each matching rule.</p>
          {!hasQual && <p className="text-xs text-amber-500 mt-2">Valid starting positions are required for every driver before these rules can be used.</p>}
          <div className="flex flex-wrap justify-end gap-2 mt-3 mb-2">
            <button type="button" className={`border rounded-md px-3 py-1 text-sm ${T.input}`}
              onClick={() => setStartingPositionRules((old) => [...(old || []),
                ...[[1, 12, 0, 2], [13, 24, 0, 4], [25, 40, 3, cfg.roster]].map(([start_min, start_max, min_count, max_count]) => ({
                  start_min, start_max, mode: "between", count: 0, min_count, max_count,
                }))])}>+ Add 1–12 / 13–24 / 25–40 example</button>
            <button type="button" className={`border rounded-md px-3 py-1 text-sm ${T.input}`}
              onClick={() => setStartingPositionRules((old) => [...(old || []), {
                start_min: 1, start_max: 12, mode: "at_most", count: 2, min_count: 0, max_count: 2,
              }])}>+ Add range rule</button>
          </div>
          {startingPositionRules.map((r, i) => {
            const change = (patch) => setStartingPositionRules((old) => old.map((x, j) => j === i ? { ...x, ...patch } : x));
            const countSelect = (key, label) => <select aria-label={label}
              className={`w-16 border rounded-md px-2 py-1 text-sm ${T.input}`} value={r[key] ?? 0}
              onChange={(e) => change({ [key]: Number(e.target.value) })}>
              {Array.from({ length: cfg.roster + 1 }, (_, n) => <option key={n} value={n}>{n}</option>)}
            </select>;
            return <div key={i} className="flex flex-wrap gap-2 items-center border rounded-md p-2 mb-2">
              <span className="text-sm">Starting positions</span>
              <input type="number" min="1" step="1" aria-label="First starting position"
                className={`w-20 border rounded-md px-2 py-1 text-sm ${T.input}`} value={r.start_min}
                onChange={(e) => change({ start_min: e.target.value })} />
              <span className="text-sm">through</span>
              <input type="number" min="1" step="1" aria-label="Last starting position"
                className={`w-20 border rounded-md px-2 py-1 text-sm ${T.input}`} value={r.start_max}
                onChange={(e) => change({ start_max: e.target.value })} />
              <span className="text-sm">require</span>
              <select aria-label="Driver count condition" className={`border rounded-md px-2 py-1 text-sm ${T.input}`}
                value={r.mode} onChange={(e) => change({ mode: e.target.value })}>
                <option value="at_least">at least</option><option value="at_most">at most</option>
                <option value="exactly">exactly</option><option value="between">between</option>
              </select>
              {r.mode === "between" ? <>{countSelect("min_count", "Minimum drivers")}<span className="text-sm">and</span>{countSelect("max_count", "Maximum drivers")}</>
                : countSelect("count", "Required driver count")}
              <span className="text-sm">drivers</span>
              <button type="button" className="ml-auto text-sm px-2 py-1 border rounded-md"
                onClick={() => setStartingPositionRules((old) => old.filter((_, j) => j !== i))}>Remove</button>
            </div>;
          })}
        </details>

        <details className={`rounded-lg p-3 mb-2 ${T.card}`} open={!!advancedOpen?.conditional}
          onToggle={(e) => { const open = e.currentTarget.open; setAdvancedOpen((v) => ({ ...v, conditional: open })); }}>
          <summary className="cursor-pointer select-none text-sm font-semibold">Conditional Rules <span className="text-xs text-slate-400 ml-2">IF → THEN</span>
            {ifThenRules.length > 0 && <span className="ml-2 text-xs text-sky-400">{ifThenRules.length}</span>}
          </summary>
          <div className="flex justify-end mt-3 mb-2">
            <button type="button" className={`border rounded-md px-3 py-1 text-sm ${T.input}`}
              onClick={() => setIfThenRules((old) => [...(old || []), {
                if_selector: emptyNascarSelector(), if_mode: "at_least", if_count: 1,
                then_selector: emptyNascarSelector(), then_mode: "at_least", then_count: 1,
              }])}>+ Add rule</button>
          </div>
          {!hasQual && <p className="text-xs text-slate-400 mb-2">Starting position becomes available after all drivers have qualifying positions.</p>}
          {(ifThenRules || []).map((r, i) => {
            const update = (patch) => setIfThenRules((old) => old.map((x, j) => j === i ? { ...x, ...patch } : x));
            const side = (key) => <div className="flex-1 min-w-[280px]">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="font-semibold text-sm">{key === "if" ? "IF lineup has" : "THEN require"}</span>
                <select className={`border rounded-md px-2 py-1 text-sm ${T.input}`} value={r[`${key}_mode`] || "at_least"}
                  onChange={(e) => update({ [`${key}_mode`]: e.target.value })}>
                  <option value="at_least">at least</option><option value="at_most">at most</option><option value="exactly">exactly</option>
                </select>
                <input type="number" min="0" max={cfg.roster} className={`w-16 border rounded-md px-2 py-1 text-sm ${T.input}`}
                  value={r[`${key}_count`] ?? 1} onChange={(e) => update({ [`${key}_count`]: e.target.value })} />
              </div>
              <NascarRuleSelector selector={r[`${key}_selector`]} onChange={(s) => update({ [`${key}_selector`]: s })}
                allDrivers={allDriverNames} teams={teamOptions} manufacturers={manufacturerOptions}
                hasQual={hasQual} isDark={isDark} inputClass={`border rounded-md px-2 py-1 text-sm ${T.input}`} />
            </div>;
            return <div key={i} className="border rounded-md p-3 mb-3">
              <div className="flex flex-wrap gap-4">{side("if")}{side("then")}</div>
              <div className="flex justify-end mt-2"><button type="button" className="text-sm px-2 py-1 border rounded-md"
                onClick={() => setIfThenRules((old) => old.filter((_, j) => j !== i))}>Delete</button></div>
            </div>;
          })}
        </details>
      </div>

      {/* Player Groups */}
      <div className="w-full">
        <details className={`rounded-lg p-3 mb-2 ${T.card}`} open={!!advancedOpen?.groups}
          onToggle={(e) => { const open = e.currentTarget.open; setAdvancedOpen((v) => ({ ...v, groups: open })); }}>
          <summary className="cursor-pointer select-none text-sm font-semibold">Driver Groups
            {groups.length > 0 && <span className="ml-2 text-xs text-sky-400">{groups.length}</span>}
          </summary>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs text-slate-400 mt-3">Set lineup counts for selected drivers.</h3>
            <button
              className={
                "px-3 py-1.5 rounded-md border text-sm transition " +
                (isDark ? "bg-slate-900/60 border-slate-700 text-slate-100 hover:bg-slate-800/70" : "bg-white border-slate-300 text-slate-900 hover:bg-gray-50")
              }
              onClick={() => setGroups((G) => [...(G || []), { mode: "at_most", count: 1, players: [] }])}
              type="button"
            >
              + Add group
            </button>
          </div>

          {(groups || []).length === 0 ? (
            <div className="text-sm text-gray-500">No groups yet. Click “Add group”.</div>
          ) : (
            <div className="space-y-3">
              {(groups || []).map((g, idx) => (
                <div key={idx} className="rounded-md border px-3 py-2">
                  <div className="flex items-center gap-2 mb-2">
                    <select
                      value={g.mode || "at_most"}
                      onChange={(e) => setGroups((Gs) => (Gs || []).map((gg, i) => (i === idx ? { ...gg, mode: e.target.value } : gg)))}
                      className={`border rounded-md px-2 py-1 text-sm ${T.input}`}
                      title="Group rule"
                    >
                      <option value="at_most">At Most</option>
                      <option value="at_least">At Least</option>
                      <option value="exactly">Exactly</option>
                    </select>

                    <input
                      type="number"
                      min={0}
                      className={`w-16 border rounded-md px-2 py-1 text-sm ${T.input}`}
                      value={g.count ?? 1}
                      onChange={(e) =>
                        setGroups((Gs) => (Gs || []).map((gg, i) => (i === idx ? { ...gg, count: Math.max(0, Number(e.target.value) || 0) } : gg)))
                      }
                      title="Number of players from the group"
                    />

                    <button
                      className={
                        "ml-auto px-2.5 py-1 rounded-md border text-sm transition " +
                        (isDark ? "bg-slate-900/60 border-slate-700 text-slate-100 hover:bg-red-900/40" : "bg-white border-slate-300 text-slate-900 hover:bg-red-50")
                      }
                      onClick={() => setGroups((Gs) => (Gs || []).filter((_, i) => i !== idx))}
                      type="button"
                    >
                      Remove
                    </button>
                  </div>

                  <DriverMultiPicker
                    allDrivers={allDriverNames}
                    value={Array.isArray(g.players) ? g.players : []}
                    onChange={(players) => setGroups((Gs) => (Gs || []).map((gg, i) => (i === idx ? { ...gg, players } : gg)))}
                    placeholder="Type to search drivers and click to add…"
                    isDark={isDark}
                  />
                </div>
              ))}
            </div>
          )}
        </details>
      </div>

      {/* Driver Pair Caps */}
<div className="w-full">
  <details className={`rounded-lg p-3 mb-2 ${T.card}`} open={!!advancedOpen?.pairCaps}
    onToggle={(e) => { const open = e.currentTarget.open; setAdvancedOpen((v) => ({ ...v, pairCaps: open })); }}>
    <summary className="cursor-pointer select-none text-sm font-semibold">Driver Pair Caps
      {pairCaps.length > 0 && <span className="ml-2 text-xs text-sky-400">{pairCaps.length}</span>}
    </summary>
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-xs text-slate-400 mt-3">Limit how often two drivers appear together.</h3>
      <button
        className={
          "px-3 py-1.5 rounded-md border text-sm transition " +
          (isDark
            ? "bg-slate-900/60 border-slate-700 text-slate-100 hover:bg-slate-800/70"
            : "bg-white border-slate-300 text-slate-900 hover:bg-gray-50")
        }
        onClick={() =>
          setPairCaps((prev) => [...(prev || []), { players: [], maxPct: 25 }])
        }
        type="button"
      >
        + Add pair cap
      </button>
    </div>

    {(pairCaps || []).length === 0 ? (
      <div className="text-sm text-gray-500">
        No pair caps yet. Example: Larson + Hamlin max 25%.
      </div>
    ) : (
      <div className="space-y-3">
        {(pairCaps || []).map((p, idx) => {
          const players = Array.isArray(p.players) ? p.players : [];
          const lineupTotal = Math.max(1, Number(numLineups) || 1);
          const maxAllowed = Math.floor((lineupTotal * (Number(p.maxPct) || 0)) / 100);

          return (
            <div key={idx} className="rounded-md border px-3 py-2">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-start">
                <div className="lg:col-span-8">
                  <DriverMultiPicker
                    allDrivers={allDriverNames}
                    value={players}
                    onChange={(nextPlayers) =>
                      setPairCaps((prev) =>
                        (prev || []).map((pp, i) =>
                          i === idx
                            ? {
                                ...pp,
                                players: [...nextPlayers].slice(0, 2),
                              }
                            : pp
                        )
                      )
                    }
                    placeholder="Add exactly 2 drivers…"
                    isDark={isDark}
                  />
                </div>

                <div className="lg:col-span-2">
                  <label className="block text-[11px] text-gray-500 mb-1">Max Pair %</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    className={`w-full border rounded-md px-2 py-1 text-sm ${T.input}`}
                    value={p.maxPct ?? 25}
                    onChange={(e) =>
                      setPairCaps((prev) =>
                        (prev || []).map((pp, i) =>
                          i === idx
                            ? { ...pp, maxPct: clamp(Number(e.target.value) || 0, 0, 100) }
                            : pp
                        )
                      )
                    }
                  />
                </div>

                <div className="lg:col-span-2 flex lg:justify-end">
                  <button
                    className={
                      "px-2.5 py-1 rounded-md border text-sm transition mt-5 " +
                      (isDark
                        ? "bg-slate-900/60 border-slate-700 text-slate-100 hover:bg-red-900/40"
                        : "bg-white border-slate-300 text-slate-900 hover:bg-red-50")
                    }
                    onClick={() =>
                      setPairCaps((prev) => (prev || []).filter((_, i) => i !== idx))
                    }
                    type="button"
                  >
                    Remove
                  </button>
                </div>
              </div>

              <div className={`mt-2 text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                {players.length === 2
                  ? `This pair can appear together in at most ${maxAllowed} of ${lineupTotal} lineups.`
                  : "Select exactly 2 drivers for this pair cap."}
              </div>
            </div>
          );
        })}
      </div>
    )}
  </details>
</div>

          </div>
        </details>
      </div>

<div className="w-full px-4 md:px-6 mb-3">
  <section className={`rounded-2xl p-3 ${panelClass}`} aria-label="Optimize">
        <div className="flex flex-wrap items-center gap-3">
          <button className="h-9 px-6 rounded-md bg-sky-500 text-slate-950 hover:bg-sky-400 text-sm font-semibold whitespace-nowrap" onClick={optimize} type="button">
            {`Optimize ${numLineups}`}
          </button>
          <div className={`flex-1 min-w-[100px] max-w-[420px] h-2 rounded-full overflow-hidden ${isDark ? "bg-slate-800" : "bg-slate-200"}`}>
            <div
              className="h-full bg-sky-500 rounded-full transition-all duration-300"
              style={{
                width: `${(Math.min(progressUI, Math.max(1, Number(numLineups) || 1)) / Math.max(1, Number(numLineups) || 1)) * 100}%`,
              }}
            />
          </div>
          <div className="text-sm text-gray-500 min-w-[45px] text-right">
            {progressUI}/{numLineups}
          </div>
        </div>

    <details className={`mt-3 border-t pt-3 ${isDark ? "border-slate-800" : "border-slate-200"}`}>
      <summary className="cursor-pointer select-none text-sm font-semibold">Reset Tools</summary>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            className={
              "px-3 py-1.5 text-xs md:text-sm rounded-lg border transition font-medium " +
              (isDark ? "bg-red-900/30 border-red-700/60 text-red-200 hover:bg-red-900/45 hover:text-red-100" : "bg-red-50 border-red-300 text-red-700 hover:bg-red-100")
            }
            onClick={resetEverything}
            title="Reset EVERYTHING to defaults"
            type="button"
          >
            Reset All
          </button>

          <button
            className={
              "px-2.5 py-1.5 text-xs md:text-sm rounded-lg border transition " +
              (isDark ? "bg-slate-900/80 border-slate-700 text-slate-200 hover:bg-slate-800" : "bg-white border-slate-300 text-slate-700 hover:bg-gray-100")
            }
            onClick={() => setLocksArr([])}
            type="button"
          >
            Clear locks
          </button>

          <button
            className={
              "px-2.5 py-1.5 text-xs md:text-sm rounded-lg border transition " +
              (isDark ? "bg-slate-900/80 border-slate-700 text-slate-200 hover:bg-slate-800" : "bg-white border-slate-300 text-slate-700 hover:bg-gray-100")
            }
            onClick={() => setExclsArr([])}
            type="button"
          >
            Clear excludes
          </button>

          <button
            className={
              "px-2.5 py-1.5 text-xs md:text-sm rounded-lg border transition " +
              (isDark ? "bg-slate-900/80 border-slate-700 text-slate-200 hover:bg-slate-800" : "bg-white border-slate-300 text-slate-700 hover:bg-gray-100")
            }
            onClick={resetExposuresOnly}
            type="button"
          >
            Clear exposures
          </button>

                    <button
            className={
              "px-2.5 py-1.5 text-xs md:text-sm rounded-lg border transition " +
              (isDark
                ? "bg-slate-900/80 border-slate-700 text-slate-200 hover:bg-slate-800"
                : "bg-white border-slate-300 text-slate-700 hover:bg-gray-100")
            }
            onClick={() => setBoost({})}
            type="button"
          >
            Clear boosts
          </button>

          <button
            className={
              "px-2.5 py-1.5 text-xs md:text-sm rounded-lg border transition " +
              (isDark ? "bg-slate-900/80 border-slate-700 text-slate-200 hover:bg-slate-800" : "bg-white border-slate-300 text-slate-700 hover:bg-gray-100")
            }
            onClick={resetConstraints}
            type="button"
          >
            Clear all
          </button>
        </div>
    </details>
  </section>
</div>

      {/* ✅ Builds bar (chips + Clear All Builds) — always visible when builds exist */}
      {Array.isArray(builds) && builds.length > 0 && (
        <div className="w-full px-4 md:px-6 mb-3">
          <div
            className={
              "rounded-xl border px-3 py-2 flex flex-wrap items-center gap-2 " +
              (isDark ? "bg-slate-950/60 border-slate-800" : "bg-white border-slate-200")
            }
          >
            <div className="flex flex-wrap gap-2">
              {builds.map((b) => {
                const active = b.id === activeBuildId;
                const isEditing = editingBuildId === b.id;

                return (
                  <div
                    key={b.id}
                    onClick={() => {
                      if (!isEditing) loadBuild(b.id);
                    }}
                    onDoubleClick={() => {
                      setEditingBuildId(b.id);
                      setEditingName(b.name);
                    }}
                    className={
                      "px-3 py-1.5 rounded-full text-sm font-medium border cursor-pointer " +
                      "flex items-center gap-2 transition " +
                      (isDark
                        ? active
                          ? "bg-blue-600/35 text-blue-100 border-blue-400/40 shadow-sm hover:bg-blue-600/45"
                          : "bg-slate-900/60 text-slate-200 border-slate-700 hover:bg-slate-800/80 hover:text-slate-100"
                        : active
                        ? "bg-blue-100 text-blue-900 border-blue-300 shadow-sm hover:bg-blue-200"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100")
                    }
                  >
                    {isEditing ? (
                      <input
                        autoFocus
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onBlur={() => {
                          if (editingName.trim()) renameBuild(b.id, editingName.trim());
                          setEditingBuildId(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.currentTarget.blur();
                          if (e.key === "Escape") setEditingBuildId(null);
                        }}
                        className={
                          "bg-transparent outline-none border-b text-sm min-w-[60px] " +
                          (isDark ? "border-blue-300/50 text-blue-100 placeholder:text-blue-200/60 caret-blue-100" : "border-slate-400 text-slate-900 placeholder:text-slate-400 caret-slate-900")
                        }
                      />
                    ) : (
                      <span title="Double-click to rename">{b.name}</span>
                    )}

                    {!isEditing && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteBuild(b.id);
                        }}
                        className={"text-xs transition " + (isDark ? "text-blue-200/60 hover:text-blue-100" : "text-slate-400 hover:text-slate-700")}
                        title="Delete build"
                        type="button"
                      >
                        ×
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="ml-auto">
              <button
                className={
                  "px-3 py-1.5 text-sm border rounded-md transition " +
                  (isDark ? "bg-slate-900/60 border-slate-700 text-slate-200 hover:bg-slate-800/80 hover:text-slate-100" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50")
                }
                  onClick={() => {
                    setBuilds([]);
                    setActiveBuildId(null);
                    setLineups([]);
                    setProgressActual(0);
                    setProgressUI(0);
                    setStopInfo(null);
                    setSelectedExposureDriver(null);
                  }}
                type="button"
              >
                Clear All Builds
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Player pool */}
      <div className="w-full px-4 md:px-6 mb-4">
        <section className={`w-full rounded-2xl overflow-hidden ${panelClass}`} aria-label="Player pool">
          <div className={`p-3 border-b ${isDark ? "border-slate-800" : "border-slate-200"}`}>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">Player Pool</h2>
              <span className={`text-xs ${isDark ? "text-slate-400" : "text-slate-600"}`}>{displayRows.length.toLocaleString()} drivers shown</span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" aria-pressed="true" title="Driver position" className="pb-2 border-b-2 border-sky-400 text-sky-500 text-xs font-semibold">D</button>
              <div className="w-full sm:w-auto sm:ml-auto">
        <input
          className={
            "border rounded-md px-3 py-1.5 w-full sm:w-80 text-sm transition " +
            (isDark ? "bg-slate-900/80 border-slate-700 text-slate-100 placeholder:text-slate-500" : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-500")
          }
          placeholder="Search driver…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
              </div>
            </div>
          </div>
          <div className="max-h-[520px] overflow-auto">
            <table className={`w-full border-separate ${textSz} ${isDark ? "text-slate-100" : "text-slate-900"}`} style={{ borderSpacing: 0 }}>
              <thead className={`${isDark ? "bg-slate-900" : "bg-gray-50"} sticky top-0 z-20 shadow-sm`}>
                <tr>
                  {TABLE_COLS.map(({ key, label, sortable }) => (
                    <th
                      key={key}
                      className={`${header} whitespace-nowrap ${sortable ? "cursor-pointer" : "cursor-default"} select-none`}
                      onClick={() => sortable && setSort(key)}
                    >
                      {label}
                      {sortable ? <span className="opacity-60">{sortArrow(key)}</span> : null}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td className={`${cell} text-gray-500`} colSpan={TABLE_COLS.length}>
                      Loading…
                    </td>
                  </tr>
                )}
                {err && (
                  <tr>
                    <td className={`${cell} text-red-600`} colSpan={TABLE_COLS.length}>
                      Failed to load: {String(err)}
                    </td>
                  </tr>
                )}
                {!loading &&
                  !err &&
                  displayRows.map((r) => {
                    const projBoosted = boostedProj(r);
                    const val = r.salary > 0 ? projBoosted / (r.salary / 1000) : NaN;
                    const boostSteps = Number(boost[r.driver] || 0);
                    const boostMult = 1 + 0.03 * boostSteps;
                    const draftKey = r.driver;
                    const isEditingProj = Object.prototype.hasOwnProperty.call(customProjDraft, draftKey);
                    const projInputValue = isEditingProj ? customProjDraft[draftKey] : fmt1(projBoosted ?? r.proj ?? 0);
                    const projDelta = Number(projBoosted || 0) - Number(r.baseProj || 0);
                    const projTone = projDelta > 0.0001 ? "up" : projDelta < -0.0001 ? "down" : "same";
                    const projInputClass =
                      "w-16 border rounded px-1.5 py-0.5 text-center tabular-nums outline-none " +
                      (projTone === "up"
                        ? isDark
                          ? "bg-emerald-500/10 border-emerald-500/40"
                          : "bg-emerald-50 border-emerald-300"
                        : projTone === "down"
                        ? isDark
                          ? "bg-red-500/10 border-red-500/40"
                          : "bg-red-50 border-red-300"
                        : isDark
                        ? "bg-slate-900/80 border-slate-700 text-slate-100 placeholder:text-slate-500"
                        : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400");
                    const projInputStyle =
                      projTone === "up"
                        ? {
                            color: isDark ? "#86efac" : "#047857",
                            WebkitTextFillColor: isDark ? "#86efac" : "#047857",
                            backgroundColor: isDark ? "rgba(16,185,129,0.14)" : "#ecfdf5",
                            borderColor: isDark ? "#34d399" : "#10b981",
                          }
                        : projTone === "down"
                        ? {
                            color: isDark ? "#fca5a5" : "#b91c1c",
                            WebkitTextFillColor: isDark ? "#fca5a5" : "#b91c1c",
                            backgroundColor: isDark ? "rgba(239,68,68,0.14)" : "#fef2f2",
                            borderColor: isDark ? "#f87171" : "#ef4444",
                          }
                        : undefined;

                    return (
                      <tr
                        key={r.driver}
                        className={
                          "transition-colors " +
                          (isDark ? "odd:bg-slate-950/70 even:bg-slate-900/60 hover:bg-slate-800/80" : "odd:bg-white even:bg-gray-50 hover:bg-blue-50/60")
                        }
                      >
                        <td className={cell}>
                          <input type="checkbox" checked={locks.has(r.driver)} onChange={() => toggleLock(r.driver)} />
                        </td>
                        <td className={cell}>
                          <input type="checkbox" checked={excls.has(r.driver)} onChange={() => toggleExcl(r.driver)} />
                        </td>
                        <td className={`${cell} tabular-nums`}>{r.qual || "—"}</td>
                        <td className={cell}>
                          <div className="inline-flex items-center gap-1">
                            <button className={`inline-flex items-center justify-center h-[18px] w-[18px] p-0 rounded border text-[8px] leading-none transition ${isDark ? "border-slate-700 text-slate-400 hover:text-emerald-300 hover:bg-slate-800" : "border-slate-300 text-slate-500 hover:text-emerald-700 hover:bg-slate-100"}`} title="+3%" onClick={() => bumpBoost(r.driver, +1)} type="button">
                              ▲
                            </button>
                            <span className="w-4 text-center text-[11px] tabular-nums">{boost[r.driver] || 0}</span>
                            <button className={`inline-flex items-center justify-center h-[18px] w-[18px] p-0 rounded border text-[8px] leading-none transition ${isDark ? "border-slate-700 text-slate-400 hover:text-rose-300 hover:bg-slate-800" : "border-slate-300 text-slate-500 hover:text-rose-700 hover:bg-slate-100"}`} title="-3%" onClick={() => bumpBoost(r.driver, -1)} type="button">
                              ▼
                            </button>
                          </div>
                        </td>
                        <td className={`${cell} whitespace-nowrap`}>{r.driver}</td>
                        <td className={`${cell} text-left`} title={r.team || "Team unavailable"}>
                          <span className="inline-block max-w-[160px] truncate align-middle">{r.team || "—"}</span>
                        </td>
                        <td className={`${cell} whitespace-nowrap`}>{r.manufacturer || "—"}</td>
                        <td className={`${cell} tabular-nums`}>{fmt0(r.salary)}</td>
                        <td className={`${cell} tabular-nums`}>
                          <div className="inline-flex items-center justify-center gap-1">
                            <input
                              type="number"
                              step="0.1"
                              inputMode="decimal"
                              className={projInputClass}
                              style={projInputStyle}
                              value={projInputValue}
                              onFocus={(e) => {
                                setCustomProjDraft((m) => {
                                  if (Object.prototype.hasOwnProperty.call(m, draftKey)) return m;
                                  return { ...m, [draftKey]: fmt1(projBoosted ?? r.proj ?? 0) };
                                });

                                requestAnimationFrame(() => e.target.select());
                              }}
                              onBlur={() => {
                                setCustomProjDraft((m) => {
                                  const next = { ...m };
                                  delete next[draftKey];
                                  return next;
                                });
                              }}
                              onChange={(e) => {
                                const raw = e.target.value;

                                setCustomProjDraft((m) => ({
                                  ...m,
                                  [draftKey]: raw,
                                }));

                                if (raw == null || String(raw).trim() === "") {
                                  resetDriverCustomProj(r.driver);
                                  return;
                                }

                                const shownProj = Number(raw);
                                const baseCustomProj =
                                  Number.isFinite(shownProj) && boostMult !== 0
                                    ? shownProj / boostMult
                                    : shownProj;

                                if (Number.isFinite(baseCustomProj)) {
                                  setDriverCustomProj(r.driver, String(baseCustomProj));
                                }
                              }}
                              title={
                                r.hasCustomProj
                                  ? `Custom projection. Original: ${fmt1(r.baseProj ?? 0)} | Boosted: ${fmt1(projBoosted)}`
                                  : "Use arrows or type to edit projection"
                              }
                            />
                            {r.hasCustomProj ? (
                              <button
                                type="button"
                                onClick={() => {
                                  resetDriverCustomProj(r.driver);
                                  setCustomProjDraft((m) => {
                                    const next = { ...m };
                                    delete next[draftKey];
                                    return next;
                                  });
                                }}
                                className={
                                  "rounded border px-1 py-0.5 text-[10px] leading-none " +
                                  (isDark
                                    ? "border-slate-700 text-slate-300 hover:bg-slate-800"
                                    : "border-slate-300 text-slate-600 hover:bg-slate-100")
                                }
                                title="Reset this projection"
                              >
                                ↺
                              </button>
                            ) : null}
                          </div>
                        </td>
                        <td className={`${cell} tabular-nums`}>{fmt1(val)}</td>
                        <td className={`${cell} tabular-nums`}>{fmt1(r.floor)}</td>
                        <td className={`${cell} tabular-nums`}>{fmt1(r.ceil)}</td>
                        <td className={`${cell} tabular-nums`}>{fmt1(r.pown * 100)}</td>
                        <td className={`${cell} tabular-nums`}>{fmt1(r.opt * 100)}</td>

                        {/* Min% */}
                        <td className={cell}>
                          <div className="inline-flex items-center gap-1">
                            <button
                              className={"px-1.5 py-0.5 border rounded " + (isDark ? "border-slate-700 text-slate-200 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-200")}
                              onClick={() =>
                                setMinPct((m) => ({
                                  ...m,
                                  [r.driver]: clamp((num(m[r.driver]) || 0) - 5, 0, 100),
                                }))
                              }
                              title="-5%"
                              type="button"
                            >
                              –
                            </button>

                            <input
                              className={"w-12 border rounded px-1.5 py-0.5 text-center " + (isDark ? "bg-slate-900/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-900")}
                              value={minPct[r.driver] ?? ""}
                              onChange={(e) =>
                                setMinPct((m) => ({
                                  ...m,
                                  [r.driver]: e.target.value === "" ? "" : clamp(num(e.target.value), 0, 100),
                                }))
                              }
                              placeholder="—"
                            />

                            <button
                              className={"px-1.5 py-0.5 border rounded " + (isDark ? "border-slate-700 text-slate-200 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-200")}
                              onClick={() =>
                                setMinPct((m) => ({
                                  ...m,
                                  [r.driver]: clamp((num(m[r.driver]) || 0) + 5, 0, 100),
                                }))
                              }
                              title="+5%"
                              type="button"
                            >
                              +
                            </button>
                          </div>
                        </td>

                        {/* Max% */}
                        <td className={cell}>
                          <div className="inline-flex items-center gap-1">
                            <button
                              className={"px-1.5 py-0.5 border rounded " + (isDark ? "border-slate-700 text-slate-200 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-200")}
                              onClick={() =>
                                setMaxPct((m) => ({
                                  ...m,
                                  [r.driver]: clamp((num(m[r.driver]) || 100) - 5, 0, 100),
                                }))
                              }
                              title="-5%"
                              type="button"
                            >
                              –
                            </button>

                            <input
                              className={"w-12 border rounded px-1.5 py-0.5 text-center " + (isDark ? "bg-slate-900/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-900")}
                              value={maxPct[r.driver] ?? ""}
                              onChange={(e) =>
                                setMaxPct((m) => ({
                                  ...m,
                                  [r.driver]: e.target.value === "" ? "" : clamp(num(e.target.value), 0, 100),
                                }))
                              }
                              placeholder="—"
                            />

                            <button
                              className={"px-1.5 py-0.5 border rounded " + (isDark ? "border-slate-700 text-slate-200 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-200")}
                              onClick={() =>
                                setMaxPct((m) => ({
                                  ...m,
                                  [r.driver]: clamp((num(m[r.driver]) || 100) + 5, 0, 100),
                                }))
                              }
                              title="+5%"
                              type="button"
                            >
                              +
                            </button>
                          </div>
                        </td>

                        <td className={cell}>{usagePct[r.driver] != null ? fmt1(usagePct[r.driver]) : "—"}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* stop-info */}
      {stopInfo && (
        <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 px-4 md:px-6">
          Stopped early at {stopInfo.produced}/{numLineups}
          {stopInfo.reason ? ` — ${stopInfo.reason}` : ""}
        </div>
      )}

      {/* results (shows whenever we have lineups) */}
      {!!lineups.length && (
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-4 w-full px-4 md:px-6">
          {/* Lineups */}
          <section className={`lg:col-span-8 rounded-lg p-3 ${T.card}`}>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-base font-bold">Lineups ({lineups.length})</h2>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1.5 border rounded text-sm"
                  onClick={() =>
                    downloadSiteLineupsCSV({
                      lineups,
                      site,
                      rosterSize: cfg.roster,
                      siteIds: siteIds || {},
                      fname: `lineups_${site.toUpperCase()}_ids.csv`,
                    })
                  }
                  type="button"
                >
                  Export CSV (IDs)
                </button>
                <button
                  className="px-3 py-1.5 border rounded text-sm"
                  onClick={() =>
                    downloadDetailedLineupsCSV({
                      lineups,
                      site,
                      rosterSize: cfg.roster,
                      siteIds: siteIds || {},
                      rows,
                      fname: `lineups_${site.toUpperCase()}_detailed.csv`,
                    })
                  }
                  type="button"
                >
                  Export CSV (Detailed)
                </button>
              </div>
            </div>

            <div className="overflow-auto max-h-[360px]">
              <table className="w-full text-[13px] border-separate" style={{ borderSpacing: 0 }}>
                <thead className={(isDark ? "bg-slate-900 text-slate-100" : "bg-gray-50 text-slate-900") + " sticky top-0 z-20 shadow-sm"}>
                  <tr>
                    <th className={header}>#</th>
                    <th className={header}>Salary</th>
                    <th className={header}>Total {optBy === "pown" || optBy === "opt" ? "Projection" : metricLabel}</th>
                    <th className={header}>Total pOWN%</th>
                    <th className={header}>Drivers (sorted by salary)</th>
                  </tr>
                </thead>
                <tbody>
                  {lineups.map((L, i) => {
                    const chosen = getChosenForLineup(L);
                    const chosenSorted = chosen.length ? [...chosen].sort((a, b) => (b?.salary || 0) - (a?.salary || 0)) : [];
                    const driversSorted = chosenSorted.length ? chosenSorted.map((r) => r.driver) : Array.isArray(L.drivers) ? [...L.drivers] : [];
                    const totalPownPct = chosenSorted.length ? chosenSorted.reduce((acc, r) => acc + (r.pown || 0) * 100, 0) : 0;

                    return (
                      <tr key={i} className={isDark ? "odd:bg-slate-950 even:bg-slate-900/70" : "odd:bg-white even:bg-gray-50"}>
                        <td className="px-2 py-1 text-center">{i + 1}</td>
                        <td className="px-2 py-1 text-center tabular-nums">{fmt0(L.salary)}</td>
                        <td className="px-2 py-1 text-center tabular-nums">{fmt1(L.total)}</td>
                        <td className="px-2 py-1 text-center tabular-nums">{fmt1(totalPownPct)}</td>
                        <td className="px-2 py-1 leading-snug">
                          <span className="break-words">
                            {driversSorted.map((d, idx) => {
                              const active = d === selectedExposureDriver;
                              return (
                                <React.Fragment key={`${d}-${idx}`}>
                                  <span
                                    className={
                                      active
                                        ? isDark
                                          ? "bg-blue-500/25 text-blue-100 px-1 rounded font-semibold"
                                          : "bg-blue-100 text-blue-900 px-1 rounded font-semibold"
                                        : ""
                                    }
                                  >
                                    {d}
                                  </span>
                                  {idx < driversSorted.length - 1 ? " • " : ""}
                                </React.Fragment>
                              );
                            })}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Exposure */}
          <section className={`lg:col-span-4 rounded-lg p-3 ${T.card}`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className={`text-base font-semibold ${T.headerText}`}>Exposure</h3>
              <button className="px-3 py-1.5 border rounded text-sm" onClick={() => downloadExposuresCSV(lineups)} type="button">
                Export Exposures
              </button>
            </div>
            <ExposureTable
              lineups={lineups}
              maxHeightClass="max-h-[440px]"
              isDark={isDark}
              selectedDriver={selectedExposureDriver}
              onSelectDriver={(driver) =>
                setSelectedExposureDriver((prev) => (prev === driver ? null : driver))
              }
            />
          </section>

          {/* Pairing Analysis */}
          <section className={`lg:col-span-4 rounded-lg p-3 ${T.card}`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className={`text-base font-semibold ${T.headerText}`}>
                {selectedExposureDriver
                  ? `Paired with ${selectedExposureDriver}`
                  : "Pairing Analysis"}
              </h3>

              {selectedExposureDriver && (
                <button
                  className="px-3 py-1.5 border rounded text-sm"
                  onClick={() => setSelectedExposureDriver(null)}
                  type="button"
                >
                  Clear
                </button>
              )}
            </div>

            {!selectedExposureDriver ? (
              <div className={isDark ? "text-slate-400 text-sm" : "text-gray-500 text-sm"}>
                Click a driver in Exposure to see their highest pairings.
              </div>
            ) : selectedDriverPairings.length === 0 ? (
              <div className={isDark ? "text-slate-400 text-sm" : "text-gray-500 text-sm"}>
                No pairings found.
              </div>
            ) : (
              <div className="overflow-auto max-h-[440px]">
                <table className="min-w-full text-[12px] border-separate" style={{ borderSpacing: 0 }}>
                  <thead className={`${isDark ? "bg-slate-900 text-slate-100" : "bg-gray-50 text-slate-900"} sticky top-0 z-20 shadow-sm`}>
                    <tr>
                      <th className={header}>Driver</th>
                      <th className={header}>Count</th>
                      <th className={header}>Pair %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedDriverPairings.map((r) => (
                      <tr
                        key={r.driver}
                        className={isDark ? "odd:bg-slate-950 even:bg-slate-900/70" : "odd:bg-white even:bg-gray-50"}
                      >
                        <td className={cell}>{r.driver}</td>
                        <td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td>
                        <td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Build Analysis */}
          <section className={`lg:col-span-12 rounded-lg p-3 ${T.card}`}>
            <button
              type="button"
              onClick={() => setShowBuildAnalysis((v) => !v)}
              className={
                "w-full flex items-center justify-between rounded-md px-2 py-2 text-left transition " +
                (isDark ? "hover:bg-slate-900/60" : "hover:bg-gray-50")
              }
            >
              <h3 className={`text-base font-semibold ${T.headerText}`}>Build Analysis</h3>
              <span className={isDark ? "text-slate-400 text-sm" : "text-gray-500 text-sm"}>
                {showBuildAnalysis ? "Hide ▲" : "Show ▼"}
              </span>
            </button>

            {showBuildAnalysis && (
              <div className="mt-3 grid grid-cols-1 xl:grid-cols-3 gap-4">
                {/* Summary */}
                <div className={`rounded-lg p-3 ${T.card}`}>
                  <h4 className={`text-sm font-semibold mb-2 ${T.headerText}`}>Summary</h4>

                  <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-[12px]">
                    <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Salary Used</div>
                    <div className="text-right tabular-nums">{fmt0(buildAnalysis.avgSalaryUsed)}</div>

                    <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Salary Left</div>
                    <div className="text-right tabular-nums">{fmt1(buildAnalysis.avgSalaryRemaining)}</div>

                    <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Projection</div>
                    <div className="text-right tabular-nums">{fmt1(buildAnalysis.avgProjection)}</div>

                    <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Start Pos</div>
                    <div className="text-right tabular-nums">{fmt1(buildAnalysis.avgStartingPosition)}</div>

                    <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Lineup pOWN%</div>
                    <div className="text-right tabular-nums">{fmt1(buildAnalysis.avgLineupPown)}</div>

                    <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Geo Mean pOWN%</div>
                    <div className="text-right tabular-nums">{fmt1(buildAnalysis.avgGeoMeanPown)}</div>
                  </div>

                  <div className="mt-4">
                    <h5 className={`text-xs font-semibold mb-2 ${T.headerText}`}>Top Drivers</h5>
                    <div className="overflow-auto max-h-[260px]">
                      <table className="min-w-full text-[12px] border-separate" style={{ borderSpacing: 0 }}>
                        <thead className={`${isDark ? "bg-slate-900 text-slate-100" : "bg-gray-50 text-slate-900"} sticky top-0 z-20 shadow-sm`}>
                          <tr>
                            <th className={header}>Driver</th>
                            <th className={header}>Count</th>
                            <th className={header}>Exp %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {buildAnalysis.topDrivers.map((r) => (
                            <tr
                              key={r.driver}
                              className={isDark ? "odd:bg-slate-950 even:bg-slate-900/70" : "odd:bg-white even:bg-gray-50"}
                            >
                              <td className={cell}>{r.driver}</td>
                              <td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td>
                              <td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Top 2-driver pairings */}
                <div className={`rounded-lg p-3 ${T.card}`}>
                  <h4 className={`text-sm font-semibold mb-2 ${T.headerText}`}>Top 2-Driver Pairings</h4>
                  <div className="overflow-auto max-h-[420px]">
                    <table className="min-w-full text-[12px] border-separate" style={{ borderSpacing: 0 }}>
                      <thead className={`${isDark ? "bg-slate-900 text-slate-100" : "bg-gray-50 text-slate-900"} sticky top-0 z-20 shadow-sm`}>
                        <tr>
                          <th className={header}>Pairing</th>
                          <th className={header}>Count</th>
                          <th className={header}>Build %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {buildAnalysis.topPairs.map((r) => (
                          <tr
                            key={r.label}
                            className={isDark ? "odd:bg-slate-950 even:bg-slate-900/70" : "odd:bg-white even:bg-gray-50"}
                          >
                            <td className={cell}>{r.label}</td>
                            <td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td>
                            <td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Top 3-driver pairings */}
                <div className={`rounded-lg p-3 ${T.card}`}>
                  <h4 className={`text-sm font-semibold mb-2 ${T.headerText}`}>Top 3-Driver Pairings</h4>
                  <div className="overflow-auto max-h-[420px]">
                    <table className="min-w-full text-[12px] border-separate" style={{ borderSpacing: 0 }}>
                      <thead className={`${isDark ? "bg-slate-900 text-slate-100" : "bg-gray-50 text-slate-900"} sticky top-0 z-20 shadow-sm`}>
                        <tr>
                          <th className={header}>Pairing</th>
                          <th className={header}>Count</th>
                          <th className={header}>Build %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {buildAnalysis.topTriples.map((r) => (
                          <tr
                            key={r.label}
                            className={isDark ? "odd:bg-slate-950 even:bg-slate-900/70" : "odd:bg-white even:bg-gray-50"}
                          >
                            <td className={cell}>{r.label}</td>
                            <td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td>
                            <td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Cards */}
          <section className="lg:col-span-12">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <h3 className="text-base font-semibold">Cards</h3>

              <div className="flex items-center gap-2">
                <span className={isDark ? "text-slate-400 text-sm" : "text-gray-600 text-sm"}>Sort lineups by:</span>

                <select className={`border rounded-md px-2 py-1.5 text-sm ${T.input}`} value={cardsSortBy} onChange={(e) => setCardsSortBy(e.target.value)}>
                  <option value="proj">Projection</option>
                  <option value="salary">Salary</option>
                  <option value="pown">pOWN%</option>
                  <option value="geo">Geo Mean%</option>
                  <option value="opt">Opt%</option>
                </select>

                <button
                  type="button"
                  onClick={() => setCardsSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                  className={
                    "border rounded-md px-3 py-1.5 text-sm transition " +
                    (isDark ? "bg-slate-900/60 border-slate-700 text-slate-100 hover:bg-slate-800/70" : "bg-white border-slate-300 text-slate-900 hover:bg-gray-50")
                  }
                  title="Toggle sort direction"
                >
                  {cardsSortDir === "asc" ? "Asc ▲" : "Desc ▼"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
              {lineupsForCards.map((L, idx) => {
                const chosen = getChosenForLineup(L);
                const chosenSorted = [...chosen].sort((a, b) => (b?.salary || 0) - (a?.salary || 0));

                const totalPownPct = chosenSorted.reduce((acc, r) => acc + (r.pown || 0) * 100, 0);
                const geoMeanPownPct = geoMeanPct(chosenSorted, (r) => (r.pown || 0) * 100);
                const zebraCell = cell + (isDark ? " bg-slate-900/40" : "");

                const containsSelected =
                  selectedExposureDriver &&
                  Array.isArray(L.drivers) &&
                  L.drivers.includes(selectedExposureDriver);

                return (
                  <div
                      key={idx}
                      className={`rounded-lg p-3 ${T.card} ${
                        containsSelected
                          ? isDark
                            ? "ring-2 ring-blue-400/60"
                            : "ring-2 ring-blue-300"
                          : ""
                      }`}
                    >
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold">Lineup #{idx + 1}</div>

                      <div className="flex items-center gap-1.5">
                        <span
                          className={
                            "text-[10px] font-medium tracking-wide " +
                            (isDark ? "text-slate-400" : "text-slate-500")
                          }
                          title="Geometric Mean pOWN%"
                        >
                          Geo Mean% {fmt1(geoMeanPownPct)}
                        </span>
                        <img src={cfg.logo} alt="" className="w-4 h-4 opacity-70" title={cfg.label} />
                      </div>
                    </div>

                    <table className="w-full text-[12px]">
                      <thead>
                        <tr className={isDark ? "text-slate-400" : "text-gray-600"}>
                          <th className={header}>Driver</th>
                          <th className={header}>Qual</th>
                          <th className={header}>pOWN%</th>
                          <th className={header}>Proj</th>
                          <th className={header}>Salary</th>
                        </tr>
                      </thead>

                      <tbody>
                        {chosenSorted.map((r, i) => (
                          <tr
                            key={r.driver}
                            className={
                              "transition-colors " +
                              (isDark
                                ? i % 2 === 0
                                  ? "bg-slate-950/70"
                                  : "bg-slate-900/60 hover:bg-slate-800/80"
                                : i % 2 === 0
                                ? "bg-white"
                                : "bg-slate-100 hover:bg-blue-50/70")
                            }
                          >
                            <td className={zebraCell}>
                              <span
                                className={
                                  r.driver === selectedExposureDriver
                                    ? isDark
                                      ? "bg-blue-500/25 text-blue-100 px-1 rounded font-semibold"
                                      : "bg-blue-100 text-blue-900 px-1 rounded font-semibold"
                                    : ""
                                }
                              >
                                {r.driver}
                              </span>
                            </td>
                            <td className={`${zebraCell} tabular-nums`}>{r.qual || "—"}</td>
                            <td className={`${zebraCell} tabular-nums`}>{fmt1((r.pown || 0) * 100)}</td>
                            <td className={`${zebraCell} tabular-nums`}>{fmt1(r.proj * (1 + 0.03 * (boost[r.driver] || 0)))}</td>
                            <td className={`${zebraCell} tabular-nums`}>{fmt0(r.salary)}</td>
                          </tr>
                        ))}

                        <tr className={isDark ? "border-t border-slate-700" : "border-t"}>
                          <td className={`${cell} font-semibold`}>Totals</td>
                          <td className={cell} />
                          <td className={`${cell} tabular-nums font-semibold`}>{fmt1(totalPownPct)}</td>
                          <td className={`${cell} tabular-nums font-semibold`}>{fmt1(L.total)}</td>
                          <td className={`${cell} tabular-nums font-semibold`}>{fmt0(L.salary)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

/* --------------------------- Exposure table --------------------------- */
function ExposureTable({
  lineups,
  maxHeightClass = "",
  isDark,
  selectedDriver,
  onSelectDriver,
}) {
  const rows = useMemo(() => {
    const m = new Map();
    for (const L of lineups) {
      for (const d of L.drivers) {
        m.set(d, (m.get(d) || 0) + 1);
      }
    }
    const total = Math.max(1, lineups.length);
    return [...m.entries()]
      .map(([driver, cnt]) => ({
        driver,
        count: cnt,
        pct: (cnt / total) * 100,
      }))
      .sort((a, b) => b.pct - a.pct || a.driver.localeCompare(b.driver));
  }, [lineups]);

  if (!rows.length) return null;

  const cell = "px-2 py-1 text-center";
  const header = "px-2 py-1 font-semibold text-center";

  return (
    <div className={`overflow-auto ${maxHeightClass}`}>
      <table className="min-w-full text-[12px] border-separate" style={{ borderSpacing: 0 }}>
        <thead className={`${isDark ? "bg-slate-900 text-slate-100" : "bg-gray-50 text-slate-900"} sticky top-0 z-20 shadow-sm`}>
          <tr>
            <th className={header}>Driver</th>
            <th className={header}>Count</th>
            <th className={header}>Exposure %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const active = selectedDriver === r.driver;

            return (
              <tr
                key={r.driver}
                onClick={() => onSelectDriver?.(r.driver)}
                className={
                  "cursor-pointer transition-colors " +
                  (isDark
                    ? active
                      ? "bg-blue-600/25"
                      : "odd:bg-slate-950 even:bg-slate-900/70 hover:bg-slate-800/80"
                    : active
                    ? "bg-blue-100"
                    : "odd:bg-white even:bg-gray-50 hover:bg-blue-50")
                }
              >
                <td className={cell}>{r.driver}</td>
                <td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td>
                <td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
